// screens/business/CompanyProfileScreen.js
import React, { useState, useEffect, useCallback } from 'react';
import {
  ScrollView, View, Text, StyleSheet, TouchableOpacity, TextInput,
  Alert, Modal, ActivityIndicator, Image, Dimensions, RefreshControl,
  ActionSheetIOS, Platform, KeyboardAvoidingView, Share,
  Switch,
 
} from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import { Ionicons } from '@expo/vector-icons';
import { LinearGradient } from 'expo-linear-gradient';
import * as ImagePicker from 'expo-image-picker';
import * as Print from 'expo-print';
import * as Sharing from 'expo-sharing';
// Clipboard — try new package, fall back to Share
let ClipboardLib = null;
try { ClipboardLib = require('@react-native-clipboard/clipboard').default; } catch (_) {}
const copyToClipboard = async (text) => {
  if (ClipboardLib) {
    ClipboardLib.setString(text);
  } else {
    // Graceful fallback — show share sheet so user can copy manually
    try { await Share.share({ message: text }); } catch (_) {}
  }
};

import { useBusiness } from '../../../context/BusinessContext';
import { storage } from '../../../utils/storage';

const { width } = Dimensions.get('window');
const API_BASE_URL = 'https://api.jobeng.ai';
const API_KEY = '16a0651c78da416445e9c2946fb72fd9a6e4ce4a71ba610db15251050c3462ef';

// ─── API helpers ─────────────────────────────────────────────────────────────
const apiRequest = async (path, method = 'GET', body = null) => {
  const token = await storage.getItem('auth_token');
  const headers = {
    'Content-Type': 'application/json',
    'X-API-Key': API_KEY,
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
  const res = await fetch(`${API_BASE_URL}${path}`, {
    method,
    headers,
    ...(body ? { body: JSON.stringify(body) } : {}),
  });
  const json = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(json.error || json.message || `HTTP ${res.status}`);
  return json;
};

const generateCompanyCode = () => {
  const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
  return Array.from({ length: 8 }, () => chars[Math.floor(Math.random() * chars.length)]).join('');
};

// ─── Top-level helper components (must NOT be inside the screen function) ──────
const InfoRow = ({ icon, label, value, onCopy }) => {
  if (!value) return null;
  return (
    <View style={styles.infoRow}>
      <View style={styles.infoIcon}><Ionicons name={icon} size={22} color="#0e4e96" /></View>
      <View style={styles.infoContent}>
        <Text style={styles.infoLabel}>{label}</Text>
        <Text style={styles.infoValue}>{value}</Text>
      </View>
      {onCopy && (
        <TouchableOpacity onPress={onCopy} style={styles.copyBtn}>
          <Ionicons name="copy-outline" size={18} color="#0e4e96" />
        </TouchableOpacity>
      )}
    </View>
  );
};

const SettingRow = ({ label, value, suffix = '' }) => (
  <View style={styles.settingRow}>
    <Text style={styles.settingLabel}>{label}</Text>
    <Text style={styles.settingValue}>{value || 'Not set'}{suffix}</Text>
  </View>
);

const Field = ({ label, value, onChange, placeholder, numeric, multiline, email, url, disabled }) => (
  <View style={styles.inputGroup}>
    <Text style={styles.inputLabel}>{label}</Text>
    <TextInput
      style={[styles.textInput, multiline && styles.textArea, disabled && styles.disabledInput]}
      value={value ?? ''}
      onChangeText={onChange}
      placeholder={placeholder}
      keyboardType={numeric ? 'numeric' : email ? 'email-address' : url ? 'url' : 'default'}
      multiline={multiline}
      numberOfLines={multiline ? 3 : 1}
      editable={!disabled}
      autoCapitalize={email || url ? 'none' : 'sentences'}
    />
  </View>
);

export default function CompanyProfileScreen({ navigation, route }) {
  const {
    companyProfile: contextCompanyProfile,
    saveCompanyProfile: contextSaveCompanyProfile,
    subscription,
    syncStatus,
  } = useBusiness();

  const { companyProfile: routeCompanyProfile } = route?.params || {};

  // ─── State ──────────────────────────────────────────────────────────────────
  const [activeTab,      setActiveTab]      = useState('overview');
  const [companyProfile, setCompanyProfile] = useState(routeCompanyProfile || contextCompanyProfile || null);
  const [loading,        setLoading]        = useState(true);
  const [refreshing,     setRefreshing]     = useState(false);
  const [saving,         setSaving]         = useState(false);
  const [syncing,        setSyncing]        = useState(false); // background re-sync indicator
  const [showEditModal,  setShowEditModal]  = useState(false);
  const modalOpenRef = React.useRef(false); // tracks modal state for applyProfile
  const loadAllRef   = React.useRef(null);   // always points to latest loadAll (avoids stale closure in focus listener)
  const isSyncingRef = React.useRef(false);  // prevents concurrent syncs
  const [editData,       setEditData]       = useState({});
  const [editSection,    setEditSection]    = useState('basic');
  const [uploadingLogo,  setUploadingLogo]  = useState(false);
  const [isOnline,       setIsOnline]       = useState(true);
  const [apiAvailable,   setApiAvailable]   = useState(true);
  const [isServerSynced, setIsServerSynced] = useState(false);
  const [userId,         setUserId]         = useState(null);

  // New feature state
  const [teamMembers,     setTeamMembers]     = useState([]);
  const [activityLog,     setActivityLog]     = useState([]);
  const [companyStats,    setCompanyStats]    = useState({});
  const [teamLoading,     setTeamLoading]     = useState(false);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [inviteEmail,     setInviteEmail]     = useState('');
  const [inviteRole,      setInviteRole]      = useState('member');
  const [sendingInvite,   setSendingInvite]   = useState(false);
  const [copiedCode,      setCopiedCode]      = useState(false);

  // PDF / Print / Preview state
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [pdfLoading, setPdfLoading] = useState(false);

  // Integration: current user profile for "me" banner + profile navigation
  const [myProfile,       setMyProfile]       = useState(null);
  const [myProfileLoading,setMyProfileLoading]= useState(false);

  // ─── Default profile ────────────────────────────────────────────────────────
  const defaultProfile = {
    id: null, name: '', email: '', phone: '', website: '', industry: '',
    companySize: '', description: '', founded: '',
    headquarters: '', logo: null, companyCode: '', companyType: '',
    address: '', city: '', postcode: '', country: 'United Kingdom', latitude: null, longitude: null,
    vatNumber: '', registrationNumber: '',
    invoicePrefix: 'INV', defaultTaxRate: '20', paymentTerms: '30',
    invoiceFooter: 'Thank you for your business. Payment is due within 30 days.',
    invoiceNotes: 'All charges are in GBP. Please quote invoice number with payment.',
    bankName: '', bankAccount: '', bankSortCode: '', bankIBAN: '', bankSWIFT: '',
    linkedIn: '', twitter: '', facebook: '', instagram: '',
    specializations: [], certifications: [], locations: [], awards: [],
    isMultiUser: false, adminEmail: '', totalEmployees: 0, annualRevenue: '',
    currency: 'GBP', timezone: 'Europe/London', fiscalYearStart: 'April',
    autoInvoiceNumbering: true, requirePONumber: false,
    complianceStatus: 'compliant', insuranceExpiry: '', cqcRegistered: false,
  };

  // ─── Connectivity ───────────────────────────────────────────────────────────
  useEffect(() => {
    const check = async () => {
      try {
        const controller = new AbortController();
        const tid = setTimeout(() => controller.abort(), 5000);
        const r = await fetch(`${API_BASE_URL}/health`, {
          method: 'GET', headers: { 'X-API-Key': API_KEY }, signal: controller.signal,
        }).catch(() => null);
        clearTimeout(tid);
        // Accept any response (even 429) as "online" — only network failure = offline
        const online = r !== null;
        setIsOnline(online); setApiAvailable(online);
      } catch { setIsOnline(true); setApiAvailable(true); } // Assume online on error
    };
    check();
    const iv = setInterval(check, 60000); // Check every 60s instead of 30s
    return () => clearInterval(iv);
  }, []);

  // ─── Bootstrap ──────────────────────────────────────────────────────────────
  useEffect(() => {
    (async () => {
      const uid = await storage.getItem('user_id');
      if (uid) setUserId(uid);
    })();
  }, []);

  useEffect(() => {
    const unsub = navigation.addListener('focus', () => {
      // Always use latest loadAll via ref — avoids stale closure
      loadAllRef.current?.(false); // false = no full-screen spinner on re-focus, just background sync
    });
    return unsub;
  }, [navigation]);

  useEffect(() => { if (userId) loadAll(); }, [userId]);

  // Handle deep-link params from BusinessUserProfileScreen
  useEffect(() => {
    if (!companyProfile?.id) return;
    if (route?.params?.openEdit)   { openEditModal('basic');   }
    if (route?.params?.openInvite) { setShowInviteModal(true); }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyProfile?.id]);

  // ─── Load everything ────────────────────────────────────────────────────────
  const loadAll = useCallback(async (showSpinner = true) => {
    if (isSyncingRef.current) return; // prevent concurrent syncs
    isSyncingRef.current = true;
    if (showSpinner) setLoading(true);
    else setSyncing(true);
    try {
      await loadCompanyProfile();
    } catch (e) { console.error(e); }
    finally {
      setLoading(false);
      setSyncing(false);
      isSyncingRef.current = false;
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // intentionally no deps — reads live values via refs/closures inside loadCompanyProfile

  // Keep ref current so focus listener always calls latest version
  useEffect(() => { loadAllRef.current = loadAll; }, [loadAll]);

  const loadCompanyProfile = async () => {
    try {
      // Always try server first — this is the sync behaviour on every navigation
      // Do a fresh connectivity check inline rather than relying on stale state
      let online = isOnline;
      let apiOk  = apiAvailable;
      if (!online) {
        try {
          const ctrl = new AbortController();
          const tid  = setTimeout(() => ctrl.abort(), 4000);
          const r    = await fetch(`${API_BASE_URL}/health`, {
            method: 'GET', headers: { 'X-API-Key': API_KEY }, signal: ctrl.signal,
          }).catch(() => null);
          clearTimeout(tid);
          online = apiOk = r !== null; // Any response = online
          setIsOnline(online); setApiAvailable(apiOk);
        } catch (_) { online = true; apiOk = true; setIsOnline(true); setApiAvailable(true); }
      }

      if (online && apiOk) {
        let srv = await fetchCompanyFromServer();
        if (!srv && !companyProfile?.id) srv = await createCompanyIfNotExists();
        if (srv) {
          applyProfile(srv);
          // Cache merged profile (including local-only fields like companyCode)
          const cachedStr = await storage.getItem('company_profile').catch(() => null);
          const cached = cachedStr ? JSON.parse(cachedStr) : {};
          const toCache = { ...cached, ...srv };
          if (!toCache.companyCode && cached.companyCode) toCache.companyCode = cached.companyCode;
          await storage.setItem('company_profile', JSON.stringify(toCache)).catch(() => {});
          await loadSideData(srv);
          return;
        }
      }

      // Offline path — use routeCompanyProfile or local cache
      if (routeCompanyProfile && !companyProfile?.id) {
        applyProfile(routeCompanyProfile); return;
      }
      const cachedStr = await storage.getItem('company_profile').catch(() => null);
      const cached = cachedStr ? JSON.parse(cachedStr) : null;
      applyProfile(cached || companyProfile || defaultProfile);
    } catch (e) {
      console.error('loadCompanyProfile:', e);
      if (!companyProfile) applyProfile(defaultProfile);
    }
  };

  const applyProfile = (profile) => {
    // Preserve local-only fields that the DB doesn't store
    const localOnlyFields = companyProfile ? {
      companyCode:    companyProfile.companyCode,
      companyType:    companyProfile.companyType,
      linkedIn:       companyProfile.linkedIn,
      twitter:        companyProfile.twitter,
      facebook:       companyProfile.facebook,
      instagram:      companyProfile.instagram,
      currency:       companyProfile.currency,
      timezone:       companyProfile.timezone,
      fiscalYearStart:companyProfile.fiscalYearStart,
      autoInvoiceNumbering: companyProfile.autoInvoiceNumbering,
      requirePONumber:companyProfile.requirePONumber,
      specializations:companyProfile.specializations,
      certifications: companyProfile.certifications,
      locations:      companyProfile.locations,
      awards:         companyProfile.awards,
    } : {};
    // Merge: incoming profile wins for DB fields, local-only preserved if incoming is empty
    const merged = { ...localOnlyFields, ...profile };
    // Restore local-only fields that got wiped by server response
    if (!merged.companyCode && localOnlyFields.companyCode) merged.companyCode = localOnlyFields.companyCode;

    setCompanyProfile(merged);
    // Only reset editData when modal is NOT currently open (avoids wiping user's in-progress edits)
    if (!modalOpenRef.current) {
      setEditData({ ...defaultProfile, ...merged });
    }
    contextSaveCompanyProfile?.(merged);
    // Keep shared storage keys in sync for other screens
    if (merged?.logo) storage.setItem('company_logo', merged.logo).catch(() => {});
    if (merged?.name) storage.setItem('company_name', merged.name).catch(() => {});
  };

  // Centralised modal opener — always primes editData and sets ref
  const openEditModal = (section = 'basic', profileOverride = null) => {
    const data = { ...defaultProfile, ...(profileOverride || companyProfile) };
    setEditData(data);
    setEditSection(section);
    modalOpenRef.current = true;
    setShowEditModal(true);
  };

  const closeEditModal = () => {
    modalOpenRef.current = false;
    setShowEditModal(false);
  };

  const loadSideData = async (profile) => {
    if (!profile?.id) return;
    loadTeamMembers(profile.id);
    loadActivityLog(profile.id);
    loadCompanyStats(profile.id);
    loadMyProfile();
  };

  const loadMyProfile = async () => {
    const uid = await storage.getItem('user_id');
    if (!uid) return;
    setMyProfileLoading(true);
    try {
      const cached = await storage.getItem('business_user_data');
      if (cached) setMyProfile(JSON.parse(cached));
      const r = await apiRequest(`/api/users/${uid}/profile`);
      const list = Array.isArray(r) ? r : (r.data ?? []);
      if (list[0]) {
        setMyProfile(list[0]);
        await storage.setItem('business_user_data', JSON.stringify(list[0]));
      }
    } catch { /* use cache */ }
    finally { setMyProfileLoading(false); }
  };

  // ─── Team members ───────────────────────────────────────────────────────────
  const loadTeamMembers = async (companyId) => {
    setTeamLoading(true);
    try {
      const res = await apiRequest(`/api/team/members?company_id=${companyId}`);
      const members = res.data ?? res.members ?? res ?? [];
      setTeamMembers(Array.isArray(members) ? members : []);
    } catch {
      // Fallback: try company_members table
      try {
        const res2 = await apiRequest(`/api/employees?company_id=${companyId}`);
        setTeamMembers(Array.isArray(res2.data) ? res2.data : []);
      } catch { setTeamMembers([]); }
    } finally { setTeamLoading(false); }
  };

  // ─── Activity log ───────────────────────────────────────────────────────────
  const loadActivityLog = async (companyId) => {
    try {
      const res = await apiRequest(`/api/data/activity_log?company_id=${companyId}&limit=30`);
      const log = res.data ?? res.activity ?? res ?? [];
      setActivityLog(Array.isArray(log) ? log.slice(0, 30) : []);
    } catch { setActivityLog([]); }
  };

  // ─── Stats ──────────────────────────────────────────────────────────────────
  const loadCompanyStats = async (companyId) => {
    try {
      // Try dedicated stats endpoint first
      const res = await apiRequest(`/api/data/company_stats?company_id=${companyId}`);
      const data = res.data ?? res ?? {};
      if (Object.keys(data).length > 0) { setCompanyStats(data); return; }
    } catch (_) {}
    // Fallback: derive stats from other API tables we know exist
    try {
      const [empRes, projRes, clientRes] = await Promise.allSettled([
        apiRequest(`/api/employees?company_id=${companyId}&limit=500`),
        apiRequest(`/api/projects?company_id=${companyId}&limit=500`),
        apiRequest(`/api/clients?company_id=${companyId}&limit=500`),
      ]);
      const derived = {};
      if (empRes.status === 'fulfilled') {
        const emp = empRes.value?.data ?? empRes.value ?? [];
        if (Array.isArray(emp)) derived.total_employees = emp.length;
      }
      if (projRes.status === 'fulfilled') {
        const proj = projRes.value?.data ?? projRes.value ?? [];
        if (Array.isArray(proj)) derived.active_projects = proj.filter(p => p.status === 'active' || !p.status).length;
      }
      if (clientRes.status === 'fulfilled') {
        const cli = clientRes.value?.data ?? clientRes.value ?? [];
        if (Array.isArray(cli)) derived.total_clients = cli.length;
      }
      setCompanyStats(derived);
    } catch (_) {
      setCompanyStats({});
    }
  };

  // ─── Server fetch / create ──────────────────────────────────────────────────
  const fetchCompanyFromServer = async () => {
    try {
      const token = await storage.getItem('auth_token');
      if (!token) return null;

      // Resolve company ID — try all possible storage keys
      let cid = companyProfile?.id
             || await storage.getItem('company_id')
             || await storage.getItem('company_id');

      if (!cid) {
        // Try from company_profile object
        try {
          const cp = await storage.getItem('company_profile');
          if (cp) { const p = JSON.parse(cp); cid = String(p.id || ''); }
        } catch {}
      }

      if (!cid) {
        // Last resort — fetch by company_id from DB
        const bizId = await storage.getItem('company_id');
        if (bizId) {
          try {
            const r    = await apiRequest(`/api/company-profiles?company_id=${bizId}`);
            const list = Array.isArray(r.data) ? r.data : (r.data ? [r.data] : []);
            if (list.length > 0) {
              cid = String(list[0].id);
              await storage.setItem('company_id', cid);
            }
          } catch {}
        }
      }

      if (!cid) { console.warn('[CompanyProfile] Could not resolve company ID'); return null; }

      const r = await apiRequest(`/api/company-profile/${cid}`);
      const c = r.data ?? r;
      if (!c || !c.id) throw new Error('No company data returned');

      const profile = mapServerToProfile(c);
      setIsServerSynced(true);
      await storage.setItem('company_id', String(c.id));
      await storage.setItem('company_id', String(c.id));
      await storage.setItem('company_profile', JSON.stringify(profile));
      await storage.setItem('company_name', profile.name || '');
      await storage.setItem('company_logo', profile.logo || '');
      console.log(`[CompanyProfile] Loaded: ${c.company_name || c.name} (id:${c.id})`);
      return profile;
    } catch (e) {
      console.error('[CompanyProfile] fetchCompanyFromServer:', e.message);
      return null;
    }
  };

  const createCompanyIfNotExists = async () => {
    try {
      const token = await storage.getItem('auth_token');
      if (!token || !userId) return null;
      const existing = await fetchCompanyFromServer();
      if (existing) return existing;

      const userEmail = await storage.getItem('user_email');
      const userName  = await storage.getItem('user_name');
      const newData = {
        name:                userName ? `${userName}'s Company` : 'My Company',
        email:               userEmail || '',
        industry:            '',
        employee_count:      1,
        phone:               '',
        website:             '',
        headquarters:        '',
        vat_number:          '',
        registration_number: '',
      };
      const r = await apiRequest('/api/company-profile', 'POST', newData);
      const c = r.data ?? r;
      const profile = mapServerToProfile(c);
      await storage.setItem('company_id', String(c.id));
      setIsServerSynced(true);
      return profile;
    } catch (e) { console.error('createCompanyIfNotExists:', e); return null; }
  };

  const mapServerToProfile = (c) => ({
    id:                 String(c.id || ''),
    name:               c.company_name || c.name || '',
    email:              c.email || '',
    phone:              c.phone || '',
    website:            c.website || '',
    industry:           c.industry || '',
    companySize:        c.employee_count ? String(c.employee_count) : '',
    logo:               c.logo || null,
    headquarters:       c.headquarters || c.address || '',
    address:            c.address || c.street_address || '',
    city:               c.city || '',
    postcode:           c.postcode || c.postal_code || c.zip_code || '',
    country:            c.country || 'United Kingdom',
    latitude:           c.latitude || null,
    longitude:          c.longitude || null,
    description:        c.description || '',
    vatNumber:          c.vat_number || '',
    registrationNumber: c.registration_number || '',
    companyCode:        c.company_code || '',
    user_id:            String(c.user_id || userId || ''),
    created_at:         c.created_at,
    updated_at:         c.updated_at,
    companyType:        c.company_type || '',
    founded:            c.founded ? String(c.founded) : '',
    linkedIn:           c.linkedin || c.linkedin_url || '',
    twitter:            c.twitter  || c.twitter_url  || '',
    facebook:           c.facebook || c.facebook_url || '',
    instagram:          '',
    bankName:           c.bank_name || '',
    bankAccount:        c.bank_account || '',
    bankSortCode:       c.bank_sort_code || '',
    bankIBAN:           c.bank_iban || '',
    bankSWIFT:          c.bank_swift || '',
    invoicePrefix:      c.invoice_prefix || 'INV',
    defaultTaxRate:     c.default_tax_rate ? String(c.default_tax_rate) : '20',
    paymentTerms:       c.payment_terms ? String(c.payment_terms) : '30',
    invoiceFooter:      c.invoice_footer || '',
    invoiceNotes:       c.invoice_notes || '',
    currency:           c.currency || 'GBP',
    autoInvoiceNumbering: c.auto_invoice_numbering !== undefined ? !!c.auto_invoice_numbering : true,
    requirePONumber:      !!c.require_po_number,
    timezone:           c.timezone || 'Europe/London',
    fiscalYearStart:    c.fiscal_year_start || 'April',
    annualRevenue:      '',
    cqcRegistered:      false,
    insuranceExpiry:    '',
    complianceStatus:   'compliant',
    specializations:    c.specializations || [],
    certifications:     c.certifications || [],
    locations:          c.locations || [],
    awards:             c.awards || [],
  });

  const mapProfileToServer = (p) => ({
    // Only columns that exist in the company_profile table
    name:                p.name,
    email:               p.email,
    industry:            p.industry,
    employee_count:      p.companySize ? parseInt(p.companySize) || null : null,
    phone:               p.phone,
    website:             p.website,
    logo:                p.logo === null ? '' : p.logo,
    headquarters:        p.headquarters,
    address:             p.address,
    city:                p.city,
    postcode:            p.postcode,
    country:             p.country,
    latitude:            p.latitude || null,
    longitude:           p.longitude || null,
    vat_number:          p.vatNumber,
    registration_number: p.registrationNumber,
    legal_name:          p.legalName || null,
    founded:             p.founded ? (parseInt(String(p.founded)) || null) : null,

    bank_name:           p.bankName,
    bank_account:        p.bankAccount,
    bank_sort_code:      p.bankSortCode,
    bank_iban:           p.bankIBAN,
    bank_swift:          p.bankSWIFT,
    invoice_prefix:      p.invoicePrefix,
    default_tax_rate:    parseFloat(p.defaultTaxRate) || 20,
    payment_terms:       parseInt(p.paymentTerms) || 30,
    invoice_footer:      p.invoiceFooter,
    invoice_notes:       p.invoiceNotes,
  });

  // ─── Save ───────────────────────────────────────────────────────────────────
  const saveCompanyProfile = async (profileData) => {
    if (!isOnline || !apiAvailable) {
      Alert.alert('No Connection', 'You need to be online to save changes.'); return false;
    }
    setSaving(true);
    try {
      const dbData = mapProfileToServer(profileData);
      // Strip nulls/undefined
      Object.keys(dbData).forEach(k => { if (k === 'logo') return; if (dbData[k] === undefined || dbData[k] === null) delete dbData[k]; });

      const method = profileData.id ? 'PUT' : 'POST';
      const path   = profileData.id ? `/api/company-profile/${profileData.id}` : '/api/company-profile';
      const r = await apiRequest(path, method, dbData);
      const serverProfile = mapServerToProfile(r.data ?? r);
      // Preserve local-only fields that the DB doesn't store
      const saved = {
        ...serverProfile,
        companyCode:    profileData.companyCode || serverProfile.companyCode || companyProfile?.companyCode || '',
        companyType:    profileData.companyType || serverProfile.companyType || companyProfile?.companyType || '',
        linkedIn:       profileData.linkedIn || serverProfile.linkedIn || '',
        twitter:        profileData.twitter || serverProfile.twitter || '',
        facebook:       profileData.facebook || serverProfile.facebook || '',
        instagram:      profileData.instagram || serverProfile.instagram || '',
        currency:       profileData.currency || serverProfile.currency || 'GBP',
        timezone:       profileData.timezone || serverProfile.timezone || 'Europe/London',
        fiscalYearStart:profileData.fiscalYearStart || serverProfile.fiscalYearStart || 'April',
        autoInvoiceNumbering: profileData.autoInvoiceNumbering !== undefined ? profileData.autoInvoiceNumbering : true,
        requirePONumber:      profileData.requirePONumber || false,
        specializations:profileData.specializations || serverProfile.specializations || [],
        certifications: profileData.certifications || serverProfile.certifications || [],
        locations:      profileData.locations || serverProfile.locations || [],
        address:        profileData.address || serverProfile.address || '',
        city:           profileData.city || serverProfile.city || '',
        postcode:       profileData.postcode || serverProfile.postcode || '',
        country:        profileData.country || serverProfile.country || 'United Kingdom',
        latitude:       profileData.latitude || serverProfile.latitude || null,
        longitude:      profileData.longitude || serverProfile.longitude || null,
        awards:         profileData.awards || serverProfile.awards || [],
      };
      setCompanyProfile(saved);
      setEditData({ ...defaultProfile, ...saved });
      contextSaveCompanyProfile?.(saved);
      setIsServerSynced(true);
      // Update AsyncStorage cache so other screens see latest data
      await storage.setItem('company_profile', JSON.stringify(saved));
      await storage.setItem('company_id', String(saved.id));
      await storage.setItem('company_id', String(saved.id));
      await storage.setItem('company_name', saved.name || '');
      await storage.setItem('company_logo', saved.logo || '');
      await loadSideData(saved);
      console.log(`[CompanyProfile] Saved: ${saved.name} (id:${saved.id})`);
      Alert.alert('Saved', 'Company profile updated successfully!');
      return true;
    } catch (e) {
      console.error('saveCompanyProfile:', e);
      Alert.alert('Error', e.message || 'Failed to save company profile');
      return false;
    } finally { setSaving(false); }
  };

  const handleSaveEdit = async () => {
    const ok = await saveCompanyProfile(editData);
    if (ok) {
      closeEditModal();
    }
  };

  // ─── Logo (gallery only, saved as base64 via saveCompanyProfile) ────────────
  const pickLogoFromGallery = async () => {
    if (!isOnline) { Alert.alert('Offline', 'Logo upload requires internet.'); return; }
    if (!companyProfile?.id) { Alert.alert('Not Found', 'Save profile first.'); return; }
    const { status } = await ImagePicker.requestMediaLibraryPermissionsAsync();
    if (status !== 'granted') { Alert.alert('Permission needed', 'Gallery access required.'); return; }
    const result = await ImagePicker.launchImageLibraryAsync({
      mediaTypes: ['images'], allowsEditing: true, aspect: [1, 1], quality: 0.6, base64: true,
    });
    if (!result.canceled && result.assets?.[0]) {
      const asset = result.assets[0];
      if (asset.fileSize && asset.fileSize > 5 * 1024 * 1024) { Alert.alert('Error', 'Image must be under 5 MB'); return; }
      setUploadingLogo(true);
      try {
        const mimeType = (asset.uri || '').toLowerCase().includes('.png') ? 'image/png' : 'image/jpeg';
        const logoData = asset.base64 ? `data:${mimeType};base64,${asset.base64}` : asset.uri;
        await saveCompanyProfile({ ...companyProfile, logo: logoData });
      } catch (e) { Alert.alert('Error', e.message || 'Logo upload failed'); }
      finally { setUploadingLogo(false); }
    }
  };

  const showImagePickerOptions = () => pickLogoFromGallery();

  const removeLogo = () => {
    Alert.alert('Remove Logo', 'Remove the company logo?', [
      { text: 'Cancel', style: 'cancel' },
      { text: 'Remove', style: 'destructive', onPress: () => saveCompanyProfile({ ...companyProfile, logo: null }) },
    ]);
  };

  // ─── Company Code & Invite ──────────────────────────────────────────────────

  // ─── PDF / Print / Preview / Share ──────────────────────────────────────────
  const buildProfileHTML = () => {
    const p = companyProfile || {};
    const stats = companyStats || {};
    const logoHTML = p.logo
      ? `<img src="${p.logo}" style="width:80px;height:80px;border-radius:40px;object-fit:cover;" />`
      : `<div style="width:80px;height:80px;border-radius:40px;background:#0e4e96;display:flex;align-items:center;justify-content:center;color:#fff;font-size:32px;font-weight:bold;">${(p.name || 'C')[0]}</div>`;

    return `
<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8" />
  <style>
    * { box-sizing: border-box; margin: 0; padding: 0; }
    body { font-family: -apple-system, 'Segoe UI', Roboto, sans-serif; color: #222; padding: 32px; background: #fff; }
    .header { display: flex; align-items: center; gap: 20px; margin-bottom: 28px; border-bottom: 2px solid #0e4e96; padding-bottom: 20px; }
    .header-info h1 { font-size: 26px; color: #0e4e96; margin-bottom: 4px; }
    .header-info p { font-size: 14px; color: #666; }
    .section { margin-bottom: 24px; }
    .section h2 { font-size: 18px; color: #0e4e96; margin-bottom: 12px; border-bottom: 1px solid #e8e8e8; padding-bottom: 6px; }
    .grid { display: flex; flex-wrap: wrap; gap: 14px; }
    .stat { flex: 1; min-width: 140px; background: #f8f9fa; border-radius: 10px; padding: 16px; text-align: center; }
    .stat .val { font-size: 22px; font-weight: bold; color: #0e4e96; }
    .stat .lab { font-size: 12px; color: #888; margin-top: 4px; }
    table { width: 100%; border-collapse: collapse; margin-top: 8px; }
    td { padding: 10px 12px; border-bottom: 1px solid #f0f0f0; font-size: 14px; }
    td:first-child { font-weight: 600; color: #555; width: 40%; }
    .chips { display: flex; flex-wrap: wrap; gap: 8px; margin-top: 8px; }
    .chip { background: #e8f0fb; color: #0e4e96; padding: 4px 12px; border-radius: 16px; font-size: 12px; font-weight: 500; }
    .footer { margin-top: 32px; text-align: center; font-size: 11px; color: #aaa; border-top: 1px solid #eee; padding-top: 12px; }
    .bank-card { background: #f0f4ff; border-radius: 10px; padding: 16px; margin-top: 8px; }
    .bank-row { display: flex; justify-content: space-between; padding: 6px 0; border-bottom: 1px solid #e8e8e8; font-size: 13px; }
    .bank-row span:first-child { color: #888; }
    .bank-row span:last-child { color: #0e4e96; font-weight: 600; }
  </style>
</head>
<body>
  <div class="header">
    ${logoHTML}
    <div class="header-info">
      <h1>${p.name || 'Company Profile'}</h1>
      <p>${p.industry || ''}${p.headquarters ? ` · ${p.headquarters}` : ''}</p>
      ${p.website ? `<p>${p.website}</p>` : ''}
    </div>
  </div>

  ${p.description ? `<div class="section"><h2>About</h2><p style="font-size:14px;color:#444;line-height:1.6;">${p.description}</p></div>` : ''}

  <div class="section">
    <h2>Key Statistics</h2>
    <div class="grid">
      <div class="stat"><div class="val">${stats.total_employees || p.companySize || '—'}</div><div class="lab">Employees</div></div>
      <div class="stat"><div class="val">${stats.active_projects || '—'}</div><div class="lab">Active Projects</div></div>
      <div class="stat"><div class="val">${stats.total_clients || '—'}</div><div class="lab">Total Clients</div></div>
      <div class="stat"><div class="val">${p.founded || '—'}</div><div class="lab">Founded</div></div>
    </div>
  </div>

  <div class="section">
    <h2>Contact Information</h2>
    <table>
      ${p.email ? `<tr><td>Email</td><td>${p.email}</td></tr>` : ''}
      ${p.phone ? `<tr><td>Phone</td><td>${p.phone}</td></tr>` : ''}
      ${p.website ? `<tr><td>Website</td><td>${p.website}</td></tr>` : ''}
      ${p.headquarters ? `<tr><td>Headquarters</td><td>${p.headquarters}</td></tr>` : ''}
      ${p.address ? `<tr><td>Address</td><td>${p.address}</td></tr>` : ''}
      ${p.city ? `<tr><td>City</td><td>${p.city}</td></tr>` : ''}
      ${p.postcode ? `<tr><td>Postcode</td><td>${p.postcode}</td></tr>` : ''}
      ${p.country ? `<tr><td>Country</td><td>${p.country}</td></tr>` : ''}
    </table>
  </div>

  <div class="section">
    <h2>Legal & Registration</h2>
    <table>
      ${p.vatNumber ? `<tr><td>VAT Number</td><td>${p.vatNumber}</td></tr>` : ''}
      ${p.registrationNumber ? `<tr><td>Registration No.</td><td>${p.registrationNumber}</td></tr>` : ''}
      ${p.companyCode ? `<tr><td>Company Code</td><td>${p.companyCode}</td></tr>` : ''}
      ${p.companyType ? `<tr><td>Company Type</td><td>${p.companyType}</td></tr>` : ''}
    </table>
  </div>

  ${p.bankName ? `
  <div class="section">
    <h2>Banking Details</h2>
    <div class="bank-card">
      <div class="bank-row"><span>Bank</span><span>${p.bankName}</span></div>
      ${p.bankAccount ? `<div class="bank-row"><span>Account</span><span>${p.bankAccount}</span></div>` : ''}
      ${p.bankSortCode ? `<div class="bank-row"><span>Sort Code</span><span>${p.bankSortCode}</span></div>` : ''}
      ${p.bankIBAN ? `<div class="bank-row"><span>IBAN</span><span>${p.bankIBAN}</span></div>` : ''}
      ${p.bankSWIFT ? `<div class="bank-row"><span>SWIFT/BIC</span><span>${p.bankSWIFT}</span></div>` : ''}
    </div>
  </div>` : ''}

  <div class="section">
    <h2>Invoice Settings</h2>
    <table>
      <tr><td>Prefix</td><td>${p.invoicePrefix || 'INV'}</td></tr>
      <tr><td>Tax Rate</td><td>${p.defaultTaxRate || 20}%</td></tr>
      <tr><td>Payment Terms</td><td>${p.paymentTerms || 30} days</td></tr>
      <tr><td>Currency</td><td>${p.currency || 'GBP'}</td></tr>
    </table>
  </div>

  ${(p.specializations || []).length > 0 ? `
  <div class="section">
    <h2>Specializations</h2>
    <div class="chips">${p.specializations.map(s => `<span class="chip">${s}</span>`).join('')}</div>
  </div>` : ''}

  ${(p.certifications || []).length > 0 ? `
  <div class="section">
    <h2>Certifications</h2>
    <div class="chips">${p.certifications.map(c => `<span class="chip">${c}</span>`).join('')}</div>
  </div>` : ''}

  ${teamMembers.length > 0 ? `
  <div class="section">
    <h2>Team Members (${teamMembers.length})</h2>
    <table>
      ${teamMembers.slice(0, 20).map(m => `<tr><td>${m.full_name || m.name || '—'}</td><td>${m.role || m.position || '—'} · ${m.email || ''}</td></tr>`).join('')}
    </table>
  </div>` : ''}

  <div class="footer">
    Generated on ${new Date().toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })} · ${p.name || 'Company'} · Powered by JOBENG
  </div>
</body>
</html>`;
  };

  const generatePDF = async () => {
    setPdfLoading(true);
    try {
      const html = buildProfileHTML();
      try {
        // Primary: generate file and share
        const { uri } = await Print.printToFileAsync({ html, base64: false });
        const available = await Sharing.isAvailableAsync();
        if (available) {
          await Sharing.shareAsync(uri, {
            mimeType: 'application/pdf',
            dialogTitle: 'Company Profile PDF',
            UTI: 'com.adobe.pdf',
          });
          return;
        }
      } catch (fileErr) {
        console.warn('printToFileAsync failed, falling back to printAsync:', fileErr?.message);
      }
      // Fallback: open system print dialog (has "Save as PDF" option)
      await Print.printAsync({ html });
    } catch (e) {
      console.error('PDF generation error:', e);
      Alert.alert('Error', 'Failed to generate PDF. Please try again.');
    } finally {
      setPdfLoading(false);
    }
  };

  const printProfile = async () => {
    try {
      const html = buildProfileHTML();
      await Print.printAsync({ html });
    } catch (e) {
      console.error('Print error:', e);
      Alert.alert('Error', 'Failed to print. Please try again.');
    }
  };

  const shareProfile = async () => {
    const p = companyProfile || {};
    const summary = [
      p.name || 'Company Profile',
      p.industry ? `Industry: ${p.industry}` : '',
      p.email ? `Email: ${p.email}` : '',
      p.phone ? `Phone: ${p.phone}` : '',
      p.website ? `Website: ${p.website}` : '',
      p.headquarters ? `HQ: ${p.headquarters}` : '',
      p.address ? `Address: ${p.address}` : '',
      p.city ? `City: ${p.city}` : '',
      p.postcode ? `Postcode: ${p.postcode}` : '',
      p.country ? `Country: ${p.country}` : '',
      p.vatNumber ? `VAT: ${p.vatNumber}` : '',
      p.companyCode ? `Company Code: ${p.companyCode}` : '',
    ].filter(Boolean).join('\n');

    try {
      await Share.share({
        title: `${p.name || 'Company'} Profile`,
        message: summary,
      });
    } catch (e) { console.error('Share error:', e); }
  };

  const showExportOptions = () => {
    const options = [
      { text: 'Cancel', style: 'cancel' },
      { text: 'Preview Profile', onPress: () => setShowPreviewModal(true) },
      { text: 'Export as PDF', onPress: generatePDF },
      { text: 'Print Profile', onPress: printProfile },
      { text: 'Share as Text', onPress: shareProfile },
    ];
    Alert.alert('Export Company Profile', 'Choose an option', options);
  };

  const renderPreviewModal = () => (
    <Modal visible={showPreviewModal} animationType="slide" onRequestClose={() => setShowPreviewModal(false)}>
      <SafeAreaView style={{ flex: 1, backgroundColor: '#fff' }}>
        <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', paddingHorizontal: 16, paddingVertical: 12, borderBottomWidth: 1, borderBottomColor: '#e8e8e8' }}>
          <TouchableOpacity onPress={() => setShowPreviewModal(false)}>
            <Ionicons name="close" size={24} color="#666" />
          </TouchableOpacity>
          <Text style={{ fontSize: 17, fontWeight: 'bold', color: '#222' }}>Profile Preview</Text>
          <View style={{ flexDirection: 'row', gap: 12 }}>
            <TouchableOpacity onPress={printProfile}>
              <Ionicons name="print" size={22} color="#0e4e96" />
            </TouchableOpacity>
            <TouchableOpacity onPress={generatePDF} disabled={pdfLoading}>
              {pdfLoading ? <ActivityIndicator size={18} color="#0e4e96" /> : <Ionicons name="document" size={22} color="#0e4e96" />}
            </TouchableOpacity>
            <TouchableOpacity onPress={shareProfile}>
              <Ionicons name="share-outline" size={22} color="#0e4e96" />
            </TouchableOpacity>
          </View>
        </View>
        <ScrollView style={{ flex: 1, padding: 20 }}>
          {/* Logo + Name */}
          <View style={{ alignItems: 'center', marginBottom: 24 }}>
            {companyProfile?.logo ? (
              <Image source={{ uri: (companyProfile.logo && companyProfile.logo.startsWith('http')) ? companyProfile.logo : undefined }} style={{ width: 90, height: 90, borderRadius: 45, marginBottom: 12 }} />
            ) : (
              <LinearGradient colors={['#0e4e96', '#2670ba']} style={{ width: 90, height: 90, borderRadius: 45, alignItems: 'center', justifyContent: 'center', marginBottom: 12 }}>
                <Ionicons name="business" size={40} color="#fff" />
              </LinearGradient>
            )}
            <Text style={{ fontSize: 24, fontWeight: 'bold', color: '#0e4e96', textAlign: 'center' }}>{companyProfile?.name || 'Company'}</Text>
            {companyProfile?.industry ? <Text style={{ fontSize: 15, color: '#666', marginTop: 4 }}>{companyProfile.industry}</Text> : null}
            {companyProfile?.headquarters ? <Text style={{ fontSize: 13, color: '#999', marginTop: 2 }}>{companyProfile.headquarters}</Text> : null}
          </View>

          {/* Stats */}
          <View style={{ flexDirection: 'row', gap: 10, marginBottom: 20 }}>
            {[
              { val: companyStats.total_employees || companyProfile?.companySize || '—', lab: 'Employees' },
              { val: companyStats.active_projects || '—', lab: 'Projects' },
              { val: companyStats.total_clients || '—', lab: 'Clients' },
            ].map((s, i) => (
              <View key={i} style={{ flex: 1, backgroundColor: '#f0f4ff', borderRadius: 10, padding: 14, alignItems: 'center' }}>
                <Text style={{ fontSize: 20, fontWeight: 'bold', color: '#0e4e96' }}>{s.val}</Text>
                <Text style={{ fontSize: 11, color: '#888', marginTop: 2 }}>{s.lab}</Text>
              </View>
            ))}
          </View>

          {/* Description */}
          {companyProfile?.description ? (
            <View style={{ backgroundColor: '#f8f9fa', borderRadius: 10, padding: 16, marginBottom: 16 }}>
              <Text style={{ fontSize: 12, color: '#888', marginBottom: 4 }}>About</Text>
              <Text style={{ fontSize: 14, color: '#333', lineHeight: 20 }}>{companyProfile.description}</Text>
            </View>
          ) : null}

          {/* Contact Details */}
          <View style={{ backgroundColor: '#fff', borderRadius: 12, padding: 16, marginBottom: 16, borderWidth: 1, borderColor: '#f0f0f0' }}>
            <Text style={{ fontSize: 16, fontWeight: '600', color: '#0e4e96', marginBottom: 12 }}>Contact</Text>
            {[
              { icon: 'mail', label: 'Email', value: companyProfile?.email },
              { icon: 'call', label: 'Phone', value: companyProfile?.phone },
              { icon: 'globe', label: 'Website', value: companyProfile?.website },
              { icon: 'location', label: 'HQ', value: companyProfile?.headquarters },
              { icon: 'navigate', label: 'City', value: companyProfile?.city },
              { icon: 'flag', label: 'Country', value: companyProfile?.country },
            ].filter(r => r.value).map((r, i) => (
              <View key={i} style={{ flexDirection: 'row', alignItems: 'center', paddingVertical: 8, borderBottomWidth: 1, borderBottomColor: '#f5f5f5' }}>
                <Ionicons name={r.icon} size={18} color="#0e4e96" style={{ marginRight: 12 }} />
                <View>
                  <Text style={{ fontSize: 11, color: '#999' }}>{r.label}</Text>
                  <Text style={{ fontSize: 14, color: '#333', fontWeight: '500' }}>{r.value}</Text>
                </View>
              </View>
            ))}
          </View>

          {/* Legal */}
          {(companyProfile?.vatNumber || companyProfile?.registrationNumber) ? (
            <View style={{ backgroundColor: '#fff', borderRadius: 12, padding: 16, marginBottom: 16, borderWidth: 1, borderColor: '#f0f0f0' }}>
              <Text style={{ fontSize: 16, fontWeight: '600', color: '#0e4e96', marginBottom: 12 }}>Legal & Registration</Text>
              {companyProfile.vatNumber ? <View style={{ paddingVertical: 6 }}><Text style={{ fontSize: 11, color: '#999' }}>VAT Number</Text><Text style={{ fontSize: 14, color: '#333', fontWeight: '500' }}>{companyProfile.vatNumber}</Text></View> : null}
              {companyProfile.registrationNumber ? <View style={{ paddingVertical: 6 }}><Text style={{ fontSize: 11, color: '#999' }}>Registration</Text><Text style={{ fontSize: 14, color: '#333', fontWeight: '500' }}>{companyProfile.registrationNumber}</Text></View> : null}
              {companyProfile.companyCode ? <View style={{ paddingVertical: 6 }}><Text style={{ fontSize: 11, color: '#999' }}>Company Code</Text><Text style={{ fontSize: 16, color: '#0e4e96', fontWeight: 'bold', letterSpacing: 2 }}>{companyProfile.companyCode}</Text></View> : null}
            </View>
          ) : null}

          {/* Banking */}
          {companyProfile?.bankName ? (
            <View style={{ backgroundColor: '#f0f4ff', borderRadius: 12, padding: 16, marginBottom: 16 }}>
              <Text style={{ fontSize: 16, fontWeight: '600', color: '#0e4e96', marginBottom: 12 }}>Banking</Text>
              {[
                { label: 'Bank', value: companyProfile.bankName },
                { label: 'Account', value: companyProfile.bankAccount },
                { label: 'Sort Code', value: companyProfile.bankSortCode },
                { label: 'IBAN', value: companyProfile.bankIBAN },
                { label: 'SWIFT', value: companyProfile.bankSWIFT },
              ].filter(r => r.value).map((r, i) => (
                <View key={i} style={{ flexDirection: 'row', justifyContent: 'space-between', paddingVertical: 6, borderBottomWidth: 1, borderBottomColor: '#e0e8f8' }}>
                  <Text style={{ fontSize: 13, color: '#888' }}>{r.label}</Text>
                  <Text style={{ fontSize: 13, color: '#0e4e96', fontWeight: '600' }}>{r.value}</Text>
                </View>
              ))}
            </View>
          ) : null}

          {/* Team snapshot */}
          {teamMembers.length > 0 ? (
            <View style={{ backgroundColor: '#fff', borderRadius: 12, padding: 16, marginBottom: 16, borderWidth: 1, borderColor: '#f0f0f0' }}>
              <Text style={{ fontSize: 16, fontWeight: '600', color: '#0e4e96', marginBottom: 12 }}>Team ({teamMembers.length})</Text>
              {teamMembers.slice(0, 10).map((m, i) => (
                <View key={m.id || i} style={{ flexDirection: 'row', alignItems: 'center', paddingVertical: 8, borderBottomWidth: 1, borderBottomColor: '#f5f5f5' }}>
                  <View style={{ width: 32, height: 32, borderRadius: 16, backgroundColor: '#0e4e96', alignItems: 'center', justifyContent: 'center', marginRight: 10 }}>
                    <Text style={{ color: '#fff', fontSize: 12, fontWeight: 'bold' }}>{(m.full_name || m.name || '?')[0]}</Text>
                  </View>
                  <View style={{ flex: 1 }}>
                    <Text style={{ fontSize: 14, fontWeight: '500', color: '#333' }}>{m.full_name || m.name}</Text>
                    <Text style={{ fontSize: 11, color: '#888' }}>{m.role || m.position || ''}{m.email ? ` · ${m.email}` : ''}</Text>
                  </View>
                </View>
              ))}
            </View>
          ) : null}

          {/* Export actions */}
          <View style={{ flexDirection: 'row', gap: 10, marginBottom: 40 }}>
            <TouchableOpacity
              onPress={generatePDF}
              disabled={pdfLoading}
              style={{ flex: 1, flexDirection: 'row', alignItems: 'center', justifyContent: 'center', backgroundColor: '#0e4e96', borderRadius: 10, paddingVertical: 14, gap: 8 }}
            >
              {pdfLoading ? <ActivityIndicator size={16} color="#fff" /> : <Ionicons name="document" size={18} color="#fff" />}
              <Text style={{ color: '#fff', fontWeight: '600', fontSize: 15 }}>Export PDF</Text>
            </TouchableOpacity>
            <TouchableOpacity
              onPress={printProfile}
              style={{ flex: 1, flexDirection: 'row', alignItems: 'center', justifyContent: 'center', backgroundColor: '#f0f4ff', borderRadius: 10, paddingVertical: 14, gap: 8, borderWidth: 1, borderColor: '#0e4e96' }}
            >
              <Ionicons name="print" size={18} color="#0e4e96" />
              <Text style={{ color: '#0e4e96', fontWeight: '600', fontSize: 15 }}>Print</Text>
            </TouchableOpacity>
          </View>
        </ScrollView>
      </SafeAreaView>
    </Modal>
  );


  const copyCompanyCode = () => {
    if (!companyProfile?.companyCode) return;
    copyToClipboard(companyProfile.companyCode);
    setCopiedCode(true);
    setTimeout(() => setCopiedCode(false), 2500);
  };

  const shareInviteLink = async () => {
    if (!companyProfile?.companyCode) { Alert.alert('Error', 'No company code found'); return; }
    const inviteLink = `https://app.jobeng.ai/join?code=${companyProfile.companyCode}`;
    try {
      await Share.share({
        message: `Join ${companyProfile.name || 'our company'} on JobEng!\n\nUse company code: ${companyProfile.companyCode}\nOr tap the link: ${inviteLink}`,
        url: inviteLink,
        title: `Join ${companyProfile.name || 'our team'} on JobEng`,
      });
    } catch (e) { console.error(e); }
  };

  const regenerateCompanyCode = async () => {
    Alert.alert(
      'Regenerate Code',
      'This will invalidate all existing invite links. Are you sure?',
      [
        { text: 'Cancel', style: 'cancel' },
        { text: 'Regenerate', style: 'destructive', onPress: async () => {
          const newCode = generateCompanyCode();
          await saveCompanyProfile({ ...companyProfile, companyCode: newCode });
        }},
      ]
    );
  };

  const sendTeamInvite = async () => {
    if (!inviteEmail.trim()) { Alert.alert('Error', 'Enter an email address'); return; }
    if (!companyProfile?.id) { Alert.alert('Error', 'Save company profile first'); return; }
    setSendingInvite(true);
    try {
      const inviteLink = `https://app.jobeng.ai/join?code=${companyProfile.companyCode}`;
      await apiRequest(`/api/data/company_invites`, 'POST', {
        company_id: companyProfile.id,
        email:        inviteEmail.trim(),
        role:         inviteRole,
        invite_link:  inviteLink,
        company_name: companyProfile.name,
        company_code: companyProfile.companyCode,
      });
      Alert.alert('Sent!', `Invite sent to ${inviteEmail.trim()}`);
      setInviteEmail(''); setShowInviteModal(false);
      loadTeamMembers(companyProfile.id);
    } catch (e) {
      // If dedicated endpoint not available, fall back to sharing
      shareInviteLink();
      setShowInviteModal(false);
    } finally { setSendingInvite(false); }
  };

  const removeMember = (memberId, memberName) => {
    Alert.alert('Remove Member', `Remove ${memberName} from the team?`, [
      { text: 'Cancel', style: 'cancel' },
      { text: 'Remove', style: 'destructive', onPress: async () => {
        try {
          await apiRequest(`/api/team/members/${memberId}`, 'DELETE');
          loadTeamMembers(companyProfile.id);
          Alert.alert('Removed', `${memberName} has been removed`);
        } catch (e) { Alert.alert('Error', e.message); }
      }},
    ]);
  };

  // ─── Refresh ────────────────────────────────────────────────────────────────
  const handleRefresh = async () => {
    setRefreshing(true);
    await loadAll();
    setRefreshing(false);
  };

  const saveLegalField = async (patch) => {
    if (!isOnline || !apiAvailable) {
      Alert.alert('Offline', 'You need to be online to save changes.'); return;
    }
    const updated = { ...companyProfile, ...patch };
    await saveCompanyProfile(updated);
  };

  // ─── InfoRow, SettingRow, Field are top-level components (defined before export) ───

  // ─── Sub-components ─────────────────────────────────────────────────────────
  const renderCompanyLogo = (size = 100, showActions = true) => (
    <View style={[styles.logoContainer, { width: size, height: size }]}>
      {companyProfile?.logo ? (
        <Image source={{ uri: (companyProfile.logo && companyProfile.logo.startsWith('http')) ? companyProfile.logo : undefined }}
          style={[styles.logoImage, { width: size, height: size, borderRadius: size / 2 }]} />
      ) : (
        <LinearGradient colors={['#0e4e96', '#2670ba']}
          style={[styles.logoPlaceholder, { width: size, height: size, borderRadius: size / 2 }]}>
          <Ionicons name="business" size={size * 0.45} color="#fff" />
        </LinearGradient>
      )}
      {showActions && (
        <View style={styles.logoActions}>
          <TouchableOpacity style={styles.logoActionBtn} onPress={showImagePickerOptions} disabled={uploadingLogo}>
            {uploadingLogo
              ? <ActivityIndicator size={16} color="#0e4e96" />
              : <Ionicons name="camera" size={15} color="#0e4e96" />}
          </TouchableOpacity>
          {companyProfile?.logo && (
            <TouchableOpacity style={[styles.logoActionBtn, { marginLeft: 4 }]} onPress={removeLogo}>
              <Ionicons name="trash" size={15} color="#f44336" />
            </TouchableOpacity>
          )}
        </View>
      )}
      {!isServerSynced && companyProfile?.id && (
        <View style={styles.syncBadge}>
          <Ionicons name="cloud-offline" size={10} color="#fff" />
        </View>
      )}
    </View>
  );

  // ─── Render Header ──────────────────────────────────────────────────────────
  const renderHeader = () => (
    <LinearGradient colors={['#0e4e96', '#2670ba']} style={styles.header}>
      <SafeAreaView edges={['top']}>
        <View style={styles.headerRow}>
          <TouchableOpacity style={styles.headerBtn} onPress={() => navigation.goBack()}>
            <Ionicons name="arrow-back" size={24} color="#fff" />
          </TouchableOpacity>
          <Text style={styles.headerTitle}>Company Profile</Text>
          <View style={{ flexDirection: 'row', gap: 8 }}>
            <TouchableOpacity style={styles.headerBtn} onPress={showExportOptions}>
              <Ionicons name="download-outline" size={24} color="#fff" />
            </TouchableOpacity>
            <TouchableOpacity style={styles.headerBtn}
              onPress={() => navigation.navigate('BusinessUserProfileScreen')}>
              <Ionicons name="person-circle-outline" size={24} color="#fff" />
            </TouchableOpacity>
            <TouchableOpacity style={styles.headerBtn} onPress={() => openEditModal('basic')}>
              <Ionicons name="create-outline" size={24} color="#fff" />
            </TouchableOpacity>
            <TouchableOpacity style={styles.headerBtn} onPress={() => navigation.replace('WelcomeBusiness', { companyProfile })}>
              <Ionicons name="home-outline" size={24} color="#fff" />
            </TouchableOpacity>
          </View>
        </View>
      </SafeAreaView>
    </LinearGradient>
  );

  // ─── Company Hero ───────────────────────────────────────────────────────────
  const renderHero = () => (
    <LinearGradient colors={['#0e4e96', '#2670ba']} style={styles.hero}>
      <View style={styles.heroContent}>
        {renderCompanyLogo(110, true)}
        <View style={styles.heroInfo}>
          <Text style={styles.heroName}>{companyProfile?.name || 'Your Company'}</Text>
          {companyProfile?.industry ? <Text style={styles.heroSub}>{companyProfile.industry}</Text> : null}
          {companyProfile?.email ? <Text style={styles.heroSub}>{companyProfile.email}</Text> : null}

          {/* Size badge */}
          {companyProfile?.companySize ? (
            <View style={styles.heroBadge}>
              <Ionicons name="people" size={12} color="#fff" />
              <Text style={styles.heroBadgeText}>{companyProfile.companySize} employees</Text>
            </View>
          ) : null}

          {/* Company Code */}
          {companyProfile?.companyCode ? (
            <TouchableOpacity style={[styles.heroBadge, { backgroundColor: 'rgba(255,255,255,0.18)' }]} onPress={copyCompanyCode}>
              <Ionicons name="key" size={12} color="#fff" />
              <Text style={styles.heroBadgeText}>
                <Text>{copiedCode ? 'Copied!' : `Code: ${companyProfile.companyCode}`}</Text>
              </Text>
              <Ionicons name="copy-outline" size={11} color="rgba(255,255,255,0.7)" style={{ marginLeft: 4 }} />
            </TouchableOpacity>
          ) : null}

          {/* Subscription */}
          {subscription ? (
            <View style={[styles.heroBadge, { backgroundColor: 'rgba(255,215,0,0.22)' }]}>
              <Ionicons name="ribbon" size={12} color="#FFD700" />
              <Text style={[styles.heroBadgeText, { color: '#FFD700' }]}>
                {subscription.plan_name || subscription.plan_id?.replace(/_/g, ' ').toUpperCase()}
              </Text>
            </View>
          ) : null}
        </View>
      </View>

      {/* Status bars */}
      {uploadingLogo && (
        <View style={styles.statusBar}>
          <ActivityIndicator size={14} color="#fff" />
          <Text style={styles.statusText}>Uploading logo…</Text>
        </View>
      )}
      {saving && (
        <View style={styles.statusBar}>
          <ActivityIndicator size={14} color="#fff" />
          <Text style={styles.statusText}>Saving…</Text>
        </View>
      )}
      {!isOnline && (
        <View style={styles.statusBar}>
          <Ionicons name="cloud-offline" size={14} color="#fff" />
          <Text style={styles.statusText}>Offline Mode</Text>
        </View>
      )}
      {(syncing || syncStatus === 'syncing') && (
        <View style={styles.statusBar}>
          <ActivityIndicator size={14} color="#fff" />
          <Text style={styles.statusText}>Syncing with server…</Text>
        </View>
      )}
    </LinearGradient>
  );

  // ─── Tab bar ────────────────────────────────────────────────────────────────
  const TABS = [
    { id: 'overview',    icon: 'business',      label: 'Overview'    },
    { id: 'team',        icon: 'people',        label: 'Team'        },
    { id: 'invoice',     icon: 'receipt',       label: 'Invoice'     },
    { id: 'bank',        icon: 'card',          label: 'Banking'     },
    { id: 'legal',       icon: 'shield-checkmark', label: 'Legal'    },
    { id: 'subscription',icon: 'ribbon',        label: 'Plan'        },
    { id: 'activity',    icon: 'time',          label: 'Activity'    },
  ];

  const renderTabBar = () => (
    <View style={styles.tabBar}>
      <ScrollView horizontal showsHorizontalScrollIndicator={false} contentContainerStyle={{ paddingHorizontal: 8 }}>
        {TABS.map(t => (
          <TouchableOpacity key={t.id} style={[styles.tab, activeTab === t.id && styles.tabActive]}
            onPress={() => setActiveTab(t.id)}>
            <Ionicons name={t.icon} size={18} color={activeTab === t.id ? '#0e4e96' : '#888'} />
            <Text style={[styles.tabText, activeTab === t.id && styles.tabTextActive]}>{t.label}</Text>
          </TouchableOpacity>
        ))}
      </ScrollView>
    </View>
  );

  // ─── Overview Tab ───────────────────────────────────────────────────────────
  const renderOverview = () => (
    <ScrollView style={styles.tabContent} refreshControl={<RefreshControl refreshing={refreshing} onRefresh={handleRefresh} />}>

      {/* Stats cards */}
      {(() => {
        // Rich fallback: API stats → profile fields → '—'
        const empVal  = companyStats.total_employees  != null ? String(companyStats.total_employees)
                      : companyProfile?.companySize   ? companyProfile.companySize
                      : null;
        const projVal = companyStats.active_projects  != null ? String(companyStats.active_projects) : null;
        const revVal  = companyStats.annual_revenue != null
                          ? `£${Number(companyStats.annual_revenue).toLocaleString()}`
                      : companyProfile?.annualRevenue
                          ? `£${Number(companyProfile.annualRevenue).toLocaleString()}`
                      : null;
        const cliVal  = companyStats.total_clients    != null ? String(companyStats.total_clients) : null;

        const cards = [
          { icon: 'people',      label: 'Employees', value: empVal,  editSection: 'basic'  },
          { icon: 'folder',      label: 'Projects',  value: projVal, editSection: null      },
          { icon: 'trending-up', label: 'Revenue',   value: revVal,  editSection: 'basic'  },
          { icon: 'person',      label: 'Clients',   value: cliVal,  editSection: null      },
        ];
        return (
          <View style={styles.statsRow}>
            {cards.map(s => (
              <TouchableOpacity
                key={s.label}
                style={styles.statCard}
                activeOpacity={s.editSection ? 0.7 : 1}
                onPress={s.editSection ? () => openEditModal(s.editSection) : undefined}>
                <Ionicons name={s.icon} size={22} color="#0e4e96" />
                <Text style={[styles.statValue, !s.value && { color: '#ccc' }]}>
                  {s.value ?? '—'}
                </Text>
                <Text style={styles.statLabel}>{s.label}</Text>
                {s.editSection && !s.value && (
                  <Text style={{ fontSize: 9, color: '#0e4e96', marginTop: 2 }}>tap to add</Text>
                )}
              </TouchableOpacity>
            ))}
          </View>
        );
      })()}

      {/* Business info */}
      <View style={styles.section}>
        <Text style={styles.sectionTitle}>Business Information</Text>
        <InfoRow icon="business"    label="Company Name"         value={companyProfile?.name} />
        <InfoRow icon="briefcase"   label="Industry"             value={companyProfile?.industry} />
        <InfoRow icon="layers"      label="Company Type"         value={companyProfile?.companyType} />
        <InfoRow icon="people"      label="Company Size"         value={companyProfile?.companySize ? `${companyProfile.companySize} employees` : null} />
        <InfoRow icon="calendar"    label="Founded"              value={companyProfile?.founded ? String(companyProfile.founded) : null} />
        <InfoRow icon="cash"        label="Annual Revenue"       value={companyProfile?.annualRevenue ? `£${companyProfile.annualRevenue}` : null} />
        <InfoRow icon="globe"       label="Currency"             value={companyProfile?.currency} />
        <InfoRow icon="time"        label="Timezone"             value={companyProfile?.timezone} />
        {companyProfile?.description ? (
          <View style={styles.descriptionBox}>
            <Text style={styles.descriptionLabel}>About</Text>
            <Text style={styles.descriptionText}>{companyProfile.description}</Text>
          </View>
        ) : null}
      </View>

      {/* Contact */}
      <View style={styles.section}>
        <Text style={styles.sectionTitle}>Contact Details</Text>
        <InfoRow icon="mail"     label="Email"         value={companyProfile?.email} onCopy={() => { copyToClipboard(companyProfile.email); Alert.alert('Copied'); }} />
        <InfoRow icon="call"     label="Phone"         value={companyProfile?.phone} />
        <InfoRow icon="globe"    label="Website"       value={companyProfile?.website} />
        <InfoRow icon="location" label="Headquarters"  value={companyProfile?.headquarters} />
        <InfoRow icon="navigate"  label="Address"       value={companyProfile?.address} />
        <InfoRow icon="business"  label="City"          value={companyProfile?.city} />
        <InfoRow icon="mail-open" label="Postcode"      value={companyProfile?.postcode} />
        <InfoRow icon="flag"      label="Country"       value={companyProfile?.country} />
      </View>

      {/* Social */}
      {(companyProfile?.linkedIn || companyProfile?.twitter || companyProfile?.facebook || companyProfile?.instagram) ? (
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Social Media</Text>
          <InfoRow icon="logo-linkedin" label="LinkedIn"  value={companyProfile?.linkedIn} />
          <InfoRow icon="logo-twitter"  label="Twitter"   value={companyProfile?.twitter} />
          <InfoRow icon="logo-facebook" label="Facebook"  value={companyProfile?.facebook} />
          <InfoRow icon="logo-instagram" label="Instagram" value={companyProfile?.instagram} />
        </View>
      ) : null}

      {/* Company Code */}
      <View style={styles.section}>
        <Text style={styles.sectionTitle}>Company Code</Text>
        <View style={styles.codeCard}>
          <View style={styles.codeBox}>
            <Text style={styles.codeText}>{companyProfile?.companyCode || '—'}</Text>
          </View>
          <Text style={styles.codeHint}>Share this code so team members can join your company on JobEng</Text>
          <Text style={styles.codeLink}>
            Invite Link: https://app.jobeng.ai/join?code={companyProfile?.companyCode || '...'}
          </Text>
          <View style={styles.codeActions}>
            <TouchableOpacity style={styles.codeActionBtn} onPress={copyCompanyCode}>
              <Ionicons name="copy-outline" size={18} color="#0e4e96" />
              <Text style={styles.codeActionText}>{copiedCode ? 'Copied!' : 'Copy Code'}</Text>
            </TouchableOpacity>
            <TouchableOpacity style={styles.codeActionBtn} onPress={shareInviteLink}>
              <Ionicons name="share-social-outline" size={18} color="#0e4e96" />
              <Text style={styles.codeActionText}>Share Link</Text>
            </TouchableOpacity>
            <TouchableOpacity style={styles.codeActionBtn} onPress={regenerateCompanyCode}>
              <Ionicons name="refresh-outline" size={18} color="#EF4444" />
              <Text style={[styles.codeActionText, { color: '#EF4444' }]}>Regenerate</Text>
            </TouchableOpacity>
          </View>
        </View>
      </View>

    </ScrollView>
  );

  // ─── Team Tab ───────────────────────────────────────────────────────────────
  const renderTeam = () => (
    <ScrollView style={styles.tabContent} refreshControl={<RefreshControl refreshing={refreshing} onRefresh={handleRefresh} />}>

      {/* Current user "me" banner — taps to open BusinessUserProfileScreen */}
      {(myProfile || myProfileLoading) && (
        <TouchableOpacity
          style={styles.myProfileBanner}
          onPress={() => navigation.navigate('BusinessUserProfileScreen')}
          activeOpacity={0.85}>
          {myProfileLoading && !myProfile
            ? <ActivityIndicator color="#0e4e96" style={{ marginRight: 12 }} />
            : myProfile?.profile_image
              ? <Image source={{ uri: myProfile.profile_image }}
                  style={{ width: 52, height: 52, borderRadius: 26, marginRight: 14 }} />
              : <LinearGradient colors={['#0e4e96','#2670ba']}
                  style={{ width: 52, height: 52, borderRadius: 26, alignItems: 'center', justifyContent: 'center', marginRight: 14 }}>
                  <Text style={{ fontSize: 20, fontWeight: 'bold', color: '#fff' }}>
                    {((myProfile?.full_name || 'Me')[0] || 'M').toUpperCase()}
                  </Text>
                </LinearGradient>}
          <View style={{ flex: 1 }}>
            <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
              <Text style={{ fontSize: 16, fontWeight: '800', color: '#0e4e96' }}>
                {myProfile?.full_name || 'My Profile'}
              </Text>
              <View style={{ backgroundColor: '#0e4e96', borderRadius: 8, paddingHorizontal: 7, paddingVertical: 2 }}>
                <Text style={{ fontSize: 10, fontWeight: 'bold', color: '#fff' }}>YOU</Text>
              </View>
            </View>
            <Text style={{ fontSize: 13, color: '#546E7A', marginTop: 2 }}>
              {myProfile?.job_title || '—'} · {myProfile?.department || '—'}
            </Text>
            <Text style={{ fontSize: 12, color: '#0e4e96', marginTop: 3, fontWeight: '600' }}>
              Tap to view & edit your profile →
            </Text>
          </View>
          <Ionicons name="chevron-forward" size={18} color="#B0BEC5" />
        </TouchableOpacity>
      )}

      {/* Invite bar */}
      <View style={styles.section}>
        <Text style={styles.sectionTitle}>Invite Team Members</Text>
        <View style={styles.inviteCodeRow}>
          <View style={styles.inviteCodeBox}>
            <Ionicons name="key" size={20} color="#0e4e96" />
            <Text style={styles.inviteCodeValue}>{companyProfile?.companyCode || '—'}</Text>
          </View>
          <TouchableOpacity style={styles.inviteShareBtn} onPress={shareInviteLink}>
            <Ionicons name="share-social" size={20} color="#fff" />
            <Text style={styles.inviteShareBtnText}>Share</Text>
          </TouchableOpacity>
        </View>
        <TouchableOpacity style={styles.primaryBtn} onPress={() => setShowInviteModal(true)}>
          <Ionicons name="mail-outline" size={20} color="#fff" />
          <Text style={styles.primaryBtnText}>Send Email Invite</Text>
        </TouchableOpacity>
      </View>

      {/* Members list */}
      <View style={styles.section}>
        <View style={styles.sectionTitleRow}>
          <Text style={styles.sectionTitle}>Team Members</Text>
          {teamLoading && <ActivityIndicator size={16} color="#0e4e96" />}
        </View>
        {teamMembers.length === 0 && !teamLoading ? (
          <View style={styles.emptyState}>
            <Ionicons name="people-outline" size={48} color="#ccc" />
            <Text style={styles.emptyText}>No team members yet</Text>
            <Text style={styles.emptySubText}>Share your company code to invite people</Text>
          </View>
        ) : teamMembers.map((m, i) => (
          <TouchableOpacity key={m.id ?? i} style={styles.memberCard}
            onPress={() => navigation.navigate('BusinessUserProfileScreen', {
              prefillUserId: m.user_id || m.id,
              prefillName:   m.name || m.email,
            })}
            activeOpacity={0.75}>
            <View style={styles.memberAvatar}>
              {m.avatar ? <Image source={{ uri: (m.avatar && m.avatar.startsWith('http')) ? m.avatar : undefined }} style={styles.memberAvatarImg} />
                : <LinearGradient colors={['#0e4e96', '#2670ba']} style={styles.memberAvatarPlaceholder}>
                    <Text style={styles.memberAvatarLetter}>{(m.name || m.email || 'U')[0].toUpperCase()}</Text>
                  </LinearGradient>}
            </View>
            <View style={styles.memberInfo}>
              <Text style={styles.memberName}>{m.name || m.email || 'Unknown'}</Text>
              <Text style={styles.memberEmail}>{m.email || ''}</Text>
              <View style={[styles.roleBadge, { backgroundColor: m.role === 'admin' ? '#0e4e96' : '#10B981' }]}>
                <Text style={styles.roleBadgeText}>{(m.role || 'member').toUpperCase()}</Text>
              </View>
            </View>
            <View style={styles.memberMeta}>
              <Text style={styles.memberStatus}>{m.status || 'active'}</Text>
              <Text style={styles.memberJoined}>{m.joined_at ? new Date(m.joined_at).toLocaleDateString() : ''}</Text>
              {m.role !== 'owner' && (
                <TouchableOpacity onPress={(e) => {
                  e.stopPropagation?.();
                  removeMember(m.id, m.name || m.email);
                }}>
                  <Ionicons name="trash-outline" size={18} color="#EF4444" />
                </TouchableOpacity>
              )}
            </View>
          </TouchableOpacity>
        ))}
      </View>
    </ScrollView>
  );

  // ─── Invoice Tab ────────────────────────────────────────────────────────────
  const renderInvoice = () => (
    <ScrollView style={styles.tabContent} refreshControl={<RefreshControl refreshing={refreshing} onRefresh={handleRefresh} />}>
      <View style={styles.section}>
        <View style={styles.sectionTitleRow}>
          <Text style={styles.sectionTitle}>Invoice Settings</Text>
          <TouchableOpacity onPress={() => openEditModal('invoice')}>
            <Ionicons name="create-outline" size={22} color="#0e4e96" />
          </TouchableOpacity>
        </View>
        <SettingRow label="Invoice Prefix"    value={companyProfile?.invoicePrefix} />
        <SettingRow label="Default Tax Rate"  value={companyProfile?.defaultTaxRate} suffix="%" />
        <SettingRow label="Payment Terms"     value={companyProfile?.paymentTerms} suffix=" days" />
        <SettingRow label="Currency"          value={companyProfile?.currency} />
        <SettingRow label="Fiscal Year Start" value={companyProfile?.fiscalYearStart} />
      </View>
      <View style={styles.section}>
        <Text style={styles.sectionTitle}>Invoice Defaults</Text>
        <View style={styles.multilineBox}>
          <Text style={styles.multilineLabel}>Footer Text</Text>
          <Text style={styles.multilineValue}>{companyProfile?.invoiceFooter || 'Not set'}</Text>
        </View>
        <View style={styles.multilineBox}>
          <Text style={styles.multilineLabel}>Default Notes</Text>
          <Text style={styles.multilineValue}>{companyProfile?.invoiceNotes || 'Not set'}</Text>
        </View>
      </View>
      <View style={styles.section}>
        <Text style={styles.sectionTitle}>Invoice Preferences</Text>
        <View style={styles.toggleRow}>
          <View style={{ flex: 1 }}>
            <Text style={styles.toggleLabel}>Auto Invoice Numbering</Text>
            <Text style={{ fontSize: 12, color: '#888', marginTop: 2 }}>Automatically number new invoices sequentially</Text>
          </View>
          <Switch
            value={!!companyProfile?.autoInvoiceNumbering}
            onValueChange={(v) => saveCompanyProfile({ ...companyProfile, autoInvoiceNumbering: v })}
            trackColor={{ false: '#ccc', true: '#0e4e96' }}
            thumbColor="#fff"
            disabled={saving}
          />
        </View>
        <View style={styles.toggleRow}>
          <View style={{ flex: 1 }}>
            <Text style={styles.toggleLabel}>Require PO Number</Text>
            <Text style={{ fontSize: 12, color: '#888', marginTop: 2 }}>Clients must provide a PO number on invoices</Text>
          </View>
          <Switch
            value={!!companyProfile?.requirePONumber}
            onValueChange={(v) => saveCompanyProfile({ ...companyProfile, requirePONumber: v })}
            trackColor={{ false: '#ccc', true: '#0e4e96' }}
            thumbColor="#fff"
            disabled={saving}
          />
        </View>
      </View>

      {/* Preview */}
      <View style={styles.section}>
        <Text style={styles.sectionTitle}>Invoice Preview</Text>
        <View style={styles.invoicePreview}>
          <View style={styles.invoicePreviewHeader}>
            {companyProfile?.logo
              ? <Image source={{ uri: (companyProfile.logo && companyProfile.logo.startsWith('http')) ? companyProfile.logo : undefined }} style={styles.invoicePreviewLogo} />
              : <View style={styles.invoicePreviewLogoPlaceholder}><Ionicons name="business" size={28} color="#0e4e96" /></View>}
            <View>
              <Text style={styles.invoicePreviewCompany}>{companyProfile?.name || 'Your Company'}</Text>
              <Text style={styles.invoicePreviewSub}>{companyProfile?.email}</Text>
              {companyProfile?.vatNumber ? <Text style={styles.invoicePreviewSub}>VAT: {companyProfile.vatNumber}</Text> : null}
            </View>
          </View>
          <View style={styles.invoicePreviewBody}>
            <Text style={styles.invoicePreviewNumber}>{companyProfile?.invoicePrefix || 'INV'}-2024-001</Text>
            <Text style={styles.invoicePreviewMeta}>Payment Terms: {companyProfile?.paymentTerms || 30} days</Text>
          </View>
          <View style={styles.invoicePreviewFooter}>
            <Text style={styles.invoicePreviewFooterText}>{companyProfile?.invoiceFooter || 'Thank you for your business.'}</Text>
          </View>
        </View>
      </View>
    </ScrollView>
  );

  // ─── Bank Tab ────────────────────────────────────────────────────────────────
  const renderBank = () => (
    <ScrollView style={styles.tabContent} refreshControl={<RefreshControl refreshing={refreshing} onRefresh={handleRefresh} />}>
      <View style={styles.section}>
        <View style={styles.sectionTitleRow}>
          <Text style={styles.sectionTitle}>Bank Details</Text>
          <TouchableOpacity onPress={() => openEditModal('bank')}>
            <Ionicons name="create-outline" size={22} color="#0e4e96" />
          </TouchableOpacity>
        </View>
        <View style={styles.bankCard}>
          <Ionicons name="card" size={32} color="#0e4e96" style={{ marginBottom: 12 }} />
          {[
            ['Bank Name',       companyProfile?.bankName],
            ['Account Number',  companyProfile?.bankAccount],
            ['Sort Code',       companyProfile?.bankSortCode],
            ['IBAN',            companyProfile?.bankIBAN],
            ['SWIFT / BIC',     companyProfile?.bankSWIFT],
          ].map(([l, v]) => (
            <View key={l} style={styles.bankRow}>
              <Text style={styles.bankLabel}>{l}</Text>
              <View style={{ flex: 1, flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' }}>
                <Text style={styles.bankValue}>{v || 'Not set'}</Text>
                {v ? (
                  <TouchableOpacity onPress={() => { copyToClipboard(v); Alert.alert('Copied', `${l} copied`); }}>
                    <Ionicons name="copy-outline" size={16} color="#0e4e96" />
                  </TouchableOpacity>
                ) : null}
              </View>
            </View>
          ))}
        </View>
      </View>

      {/* Payment API reference */}
      {companyProfile?.companyCode ? (
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>API Reference</Text>
          <View style={styles.apiRefBox}>
            <Text style={styles.apiRefLabel}>API Endpoint</Text>
            <Text style={styles.apiRefValue} selectable>
              {`/api/company-profile/${companyProfile?.id || '...'}`}
            </Text>
          </View>
        </View>
      ) : null}
    </ScrollView>
  );

  // ─── Legal Tab ──────────────────────────────────────────────────────────────
  const renderLegal = () => {
    const status = companyProfile?.complianceStatus || 'unknown';
    const statusColor =
      status === 'compliant'     ? '#10B981' :
      status === 'review'        ? '#F59E0B' : '#EF4444';
    const statusLabel =
      status === 'compliant'     ? 'Compliant' :
      status === 'review'        ? 'Under Review' :
      status === 'non_compliant' ? 'Action Required' : 'Unknown';

    return (
      <ScrollView style={styles.tabContent} refreshControl={<RefreshControl refreshing={refreshing} onRefresh={handleRefresh} />}>

        {/* ── Compliance Banner ── */}
        <View style={[styles.complianceBanner, { backgroundColor: statusColor }]}>
          <Ionicons name={status === 'compliant' ? 'shield-checkmark' : 'warning'} size={28} color="#fff" />
          <View style={{ marginLeft: 14, flex: 1 }}>
            <Text style={styles.complianceBannerTitle}>{statusLabel}</Text>
            <Text style={styles.complianceBannerSub}>Current compliance status</Text>
          </View>
          <TouchableOpacity
            style={{ backgroundColor: 'rgba(255,255,255,0.25)', borderRadius: 8, paddingHorizontal: 12, paddingVertical: 6 }}
            onPress={() => openEditModal('legal')}>
            <Text style={{ color: '#fff', fontWeight: '700', fontSize: 13 }}>Edit</Text>
          </TouchableOpacity>
        </View>

        {/* ── Compliance Status Pills (inline save) ── */}
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Compliance Status</Text>
          <Text style={{ fontSize: 13, color: '#888', marginBottom: 12 }}>Tap to update immediately</Text>
          <View style={{ flexDirection: 'row', gap: 10 }}>
            {[
              { key: 'compliant',     label: 'Compliant',     color: '#10B981' },
              { key: 'review',        label: 'Under Review',  color: '#F59E0B' },
              { key: 'non_compliant', label: 'Non-Compliant', color: '#EF4444' },
            ].map(s => {
              const active = (companyProfile?.complianceStatus || 'compliant') === s.key;
              return (
                <TouchableOpacity
                  key={s.key}
                  style={[styles.compliancePill, { borderColor: s.color, backgroundColor: active ? s.color : '#fff' }]}
                  onPress={() => saveLegalField({ complianceStatus: s.key })}
                  disabled={saving}>
                  <Ionicons
                    name={active ? 'checkmark-circle' : 'ellipse-outline'}
                    size={16}
                    color={active ? '#fff' : s.color}
                  />
                  <Text style={[styles.compliancePillText, { color: active ? '#fff' : s.color }]}>{s.label}</Text>
                </TouchableOpacity>
              );
            })}
          </View>
        </View>

        {/* ── Legal Details ── */}
        <View style={styles.section}>
          <View style={styles.sectionTitleRow}>
            <Text style={styles.sectionTitle}>Legal Details</Text>
            <TouchableOpacity onPress={() => openEditModal('legal')}>
              <Ionicons name="create-outline" size={22} color="#0e4e96" />
            </TouchableOpacity>
          </View>

          {companyProfile?.registrationNumber ? (
            <InfoRow
              icon="document-text"
              label="Registration Number"
              value={companyProfile.registrationNumber}
              onCopy={() => { copyToClipboard(companyProfile.registrationNumber); Alert.alert('Copied', 'Registration number copied'); }}
            />
          ) : (
            <TouchableOpacity style={styles.legalEmptyField}
              onPress={() => openEditModal('legal')}>
              <Ionicons name="document-text-outline" size={20} color="#ccc" />
              <Text style={styles.legalEmptyFieldText}>Add Registration Number</Text>
              <Ionicons name="add-circle-outline" size={20} color="#0e4e96" />
            </TouchableOpacity>
          )}

          {companyProfile?.vatNumber ? (
            <InfoRow
              icon="receipt"
              label="VAT Number"
              value={companyProfile.vatNumber}
              onCopy={() => { copyToClipboard(companyProfile.vatNumber); Alert.alert('Copied', 'VAT number copied'); }}
            />
          ) : (
            <TouchableOpacity style={styles.legalEmptyField}
              onPress={() => openEditModal('legal')}>
              <Ionicons name="receipt-outline" size={20} color="#ccc" />
              <Text style={styles.legalEmptyFieldText}>Add VAT Number</Text>
              <Ionicons name="add-circle-outline" size={20} color="#0e4e96" />
            </TouchableOpacity>
          )}

          {companyProfile?.companyType ? (
            <InfoRow icon="layers" label="Company Type" value={companyProfile.companyType} />
          ) : (
            <TouchableOpacity style={styles.legalEmptyField}
              onPress={() => openEditModal('legal')}>
              <Ionicons name="layers-outline" size={20} color="#ccc" />
              <Text style={styles.legalEmptyFieldText}>Add Company Type</Text>
              <Ionicons name="add-circle-outline" size={20} color="#0e4e96" />
            </TouchableOpacity>
          )}

          {companyProfile?.founded
            ? <InfoRow icon="calendar" label="Founded" value={String(companyProfile.founded)} />
            : null}
        </View>

        {/* ── Insurance & CQC ── */}
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Insurance & Certifications</Text>

          {/* CQC — interactive inline toggle */}
          <View style={styles.toggleRow}>
            <View style={{ flex: 1 }}>
              <Text style={styles.toggleLabel}>CQC Registered</Text>
              <Text style={{ fontSize: 12, color: '#888', marginTop: 2 }}>Care Quality Commission registration</Text>
            </View>
            <Switch
              value={!!companyProfile?.cqcRegistered}
              onValueChange={(v) => saveLegalField({ cqcRegistered: v })}
              trackColor={{ false: '#ccc', true: '#0e4e96' }}
              thumbColor="#fff"
              disabled={saving}
            />
          </View>

          {/* Insurance expiry */}
          {companyProfile?.insuranceExpiry ? (
            <InfoRow icon="shield-checkmark" label="Insurance Expiry" value={companyProfile.insuranceExpiry} />
          ) : (
            <TouchableOpacity style={styles.legalEmptyField}
              onPress={() => openEditModal('legal')}>
              <Ionicons name="shield-outline" size={20} color="#ccc" />
              <Text style={styles.legalEmptyFieldText}>Add Insurance Expiry Date</Text>
              <Ionicons name="add-circle-outline" size={20} color="#0e4e96" />
            </TouchableOpacity>
          )}
        </View>

        {/* ── Key Documents Summary ── */}
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Document Checklist</Text>
          {[
            {
              icon:   companyProfile?.registrationNumber ? 'checkmark-circle' : 'ellipse-outline',
              color:  companyProfile?.registrationNumber ? '#10B981' : '#ccc',
              label:  'Companies House Registration',
              status: companyProfile?.registrationNumber ? `Reg: ${companyProfile.registrationNumber}` : 'Not recorded — tap to add',
              onPress: companyProfile?.registrationNumber ? null : () => openEditModal('legal'),
            },
            {
              icon:   companyProfile?.vatNumber ? 'checkmark-circle' : 'ellipse-outline',
              color:  companyProfile?.vatNumber ? '#10B981' : '#ccc',
              label:  'VAT Registration',
              status: companyProfile?.vatNumber ? `VAT: ${companyProfile.vatNumber}` : 'Not registered — tap to add',
              onPress: companyProfile?.vatNumber ? null : () => openEditModal('legal'),
            },
            {
              icon:   companyProfile?.insuranceExpiry ? 'checkmark-circle' : 'ellipse-outline',
              color:  companyProfile?.insuranceExpiry ? '#10B981' : '#ccc',
              label:  "Employer's Liability Insurance",
              status: companyProfile?.insuranceExpiry ? `Expires: ${companyProfile.insuranceExpiry}` : 'Not recorded — tap to add',
              onPress: companyProfile?.insuranceExpiry ? null : () => openEditModal('legal'),
            },
            {
              icon:   companyProfile?.cqcRegistered ? 'checkmark-circle' : 'ellipse-outline',
              color:  companyProfile?.cqcRegistered ? '#10B981' : '#ccc',
              label:  'CQC Registration',
              status: companyProfile?.cqcRegistered ? 'Registered' : 'Not registered — toggle above to update',
              onPress: null,
            },
          ].map(d => (
            <TouchableOpacity
              key={d.label}
              style={styles.documentRow}
              onPress={d.onPress}
              activeOpacity={d.onPress ? 0.7 : 1}>
              <Ionicons name={d.icon} size={24} color={d.color} />
              <View style={{ marginLeft: 12, flex: 1 }}>
                <Text style={styles.documentLabel}>{d.label}</Text>
                <Text style={[styles.documentStatus, { color: d.color === '#ccc' ? '#aaa' : '#555' }]}>{d.status}</Text>
              </View>
              {d.onPress && <Ionicons name="add-circle-outline" size={20} color="#0e4e96" />}
            </TouchableOpacity>
          ))}
        </View>

        {/* ── Full Edit Button ── */}
        <View style={{ paddingHorizontal: 14, paddingBottom: 24 }}>
          <TouchableOpacity
            style={styles.primaryBtn}
            onPress={() => openEditModal('legal')}>
            <Ionicons name="create-outline" size={20} color="#fff" />
            <Text style={styles.primaryBtnText}>Edit All Legal Details</Text>
          </TouchableOpacity>
        </View>

      </ScrollView>
    );
  };

  // ─── Subscription Tab ───────────────────────────────────────────────────────
  const renderSubscription = () => (
    <ScrollView style={styles.tabContent} refreshControl={<RefreshControl refreshing={refreshing} onRefresh={handleRefresh} />}>
      {subscription ? (
        <>
          <LinearGradient colors={['#0e4e96', '#2670ba']} style={styles.planCard}>
            <Ionicons name="ribbon" size={40} color="#FFD700" />
            <Text style={styles.planName}>{subscription.plan_name || subscription.plan_id?.replace(/_/g, ' ').toUpperCase()}</Text>
            {subscription.price != null && <Text style={styles.planPrice}>£{subscription.price}/mo</Text>}
            <View style={[styles.heroBadge, { backgroundColor: 'rgba(255,255,255,0.15)', marginTop: 8 }]}>
              <Ionicons name="checkmark-circle" size={14} color="#4ade80" />
              <Text style={[styles.heroBadgeText, { color: '#4ade80' }]}>{subscription.status || 'Active'}</Text>
            </View>
          </LinearGradient>

          <View style={styles.section}>
            <Text style={styles.sectionTitle}>Plan Details</Text>
            {[
              ['Plan',           subscription.plan_name || subscription.plan_id],
              ['Status',         subscription.status],
              ['Start Date',     subscription.start_date ? new Date(subscription.start_date).toLocaleDateString() : null],
              ['Renewal Date',   subscription.renewal_date ? new Date(subscription.renewal_date).toLocaleDateString() : null],
              ['Billing Cycle',  subscription.billing_cycle],
              ['Price',          subscription.price != null ? `£${subscription.price}/mo` : null],
            ].filter(([,v]) => v).map(([l, v]) => <SettingRow key={l} label={l} value={v} />)}
          </View>

          {subscription.features && (
            <View style={styles.section}>
              <Text style={styles.sectionTitle}>Included Features</Text>
              {(Array.isArray(subscription.features) ? subscription.features : Object.keys(subscription.features)).map(f => (
                <View key={f} style={styles.featureRow}>
                  <Ionicons name="checkmark-circle" size={18} color="#10B981" />
                  <Text style={styles.featureText}>{typeof f === 'string' ? f : ''}</Text>
                </View>
              ))}
            </View>
          )}
        </>
      ) : (
        <View style={styles.section}>
          <View style={styles.emptyState}>
            <Ionicons name="ribbon-outline" size={52} color="#ccc" />
            <Text style={styles.emptyText}>No Active Plan</Text>
            <Text style={styles.emptySubText}>Upgrade to access all JobEng features</Text>
            <TouchableOpacity style={[styles.primaryBtn, { marginTop: 20, paddingHorizontal: 32 }]}
              onPress={() => { try { navigation.navigate('Subscription'); } catch(e) { navigation.navigate('Settings'); } }}>
              <Ionicons name="arrow-up-circle" size={20} color="#fff" />
              <Text style={styles.primaryBtnText}>View Plans</Text>
            </TouchableOpacity>
          </View>
        </View>
      )}

      <View style={styles.section}>
        <Text style={styles.sectionTitle}>Company Code & API</Text>
        <View style={styles.apiRefBox}>
          <Text style={styles.apiRefLabel}>Company Code</Text>
          <Text style={styles.apiRefValue} selectable>{companyProfile?.companyCode || '—'}</Text>
        </View>
        <View style={styles.apiRefBox}>
          <Text style={styles.apiRefLabel}>Invite Link</Text>
          <Text style={styles.apiRefValue} selectable>
            {`https://app.jobeng.ai/join?code=${companyProfile?.companyCode || '...'}`}
          </Text>
        </View>
        <View style={styles.apiRefBox}>
          <Text style={styles.apiRefLabel}>API Endpoint</Text>
          <Text style={styles.apiRefValue} selectable>
            {`/api/company-profile/${companyProfile?.id || '...'}`}
          </Text>
        </View>
      </View>
    </ScrollView>
  );

  // ─── Activity Tab ───────────────────────────────────────────────────────────
  const renderActivity = () => (
    <ScrollView style={styles.tabContent} refreshControl={<RefreshControl refreshing={refreshing} onRefresh={handleRefresh} />}>
      <View style={styles.section}>
        <Text style={styles.sectionTitle}>Recent Activity</Text>
        {activityLog.length === 0 ? (
          <View style={styles.emptyState}>
            <Ionicons name="time-outline" size={48} color="#ccc" />
            <Text style={styles.emptyText}>No activity yet</Text>
          </View>
        ) : activityLog.map((item, i) => (
          <View key={item.id ?? i} style={styles.activityRow}>
            <View style={[styles.activityDot, { backgroundColor: item.type === 'error' ? '#EF4444' : '#10B981' }]} />
            <View style={{ flex: 1, marginLeft: 12 }}>
              <Text style={styles.activityTitle}>{item.action || item.title || item.description || 'Activity'}</Text>
              <Text style={styles.activityMeta}>
                {item.user_name || item.user || ''} · {item.created_at ? new Date(item.created_at).toLocaleString() : ''}
              </Text>
            </View>
          </View>
        ))}
      </View>

      {/* Server sync info */}
      <View style={styles.section}>
        <Text style={styles.sectionTitle}>Sync Status</Text>
        {[
          ['Last Saved',    companyProfile?.updated_at ? new Date(companyProfile.updated_at).toLocaleString() : 'Never'],
          ['Created',       companyProfile?.created_at ? new Date(companyProfile.created_at).toLocaleString() : 'Unknown'],
          ['Server Synced', isServerSynced ? 'Yes' : 'No'],
          ['Online',        isOnline ? 'Yes' : 'No'],
          ['Sync Status',   syncStatus || 'idle'],
        ].map(([l, v]) => <SettingRow key={l} label={l} value={v} />)}
      </View>
    </ScrollView>
  );

  // ─── Edit Modal ─────────────────────────────────────────────────────────────
  const EDIT_SECTIONS = [
    { id: 'basic',   label: 'Basic'   },
    { id: 'contact', label: 'Contact' },
    { id: 'social',  label: 'Social'  },
    { id: 'invoice', label: 'Invoice' },
    { id: 'bank',    label: 'Bank'    },
    { id: 'legal',   label: 'Legal'   },
  ];

  // Stable field updater — avoids closure re-creation on each render
  const upd = useCallback((key) => (val) => {
    setEditData(p => ({ ...p, [key]: val }));
  }, []);

  const renderEditModal = () => (
    <Modal visible={showEditModal} animationType="slide" presentationStyle="pageSheet">
      <SafeAreaView style={styles.modalShell} edges={['top', 'bottom']}>
        <KeyboardAvoidingView style={{ flex: 1 }} behavior={Platform.OS === 'ios' ? 'padding' : 'height'}>
          {/* Modal header */}
          <View style={styles.modalHeader}>
            <TouchableOpacity onPress={closeEditModal}>
              <Text style={styles.modalCancel}>Cancel</Text>
            </TouchableOpacity>
            <Text style={styles.modalTitle}>Edit Profile</Text>
            <TouchableOpacity onPress={handleSaveEdit} disabled={saving}>
              {saving ? <ActivityIndicator size="small" color="#0e4e96" />
                : <Text style={styles.modalSave}>Save</Text>}
            </TouchableOpacity>
          </View>

          {/* Section picker */}
          <ScrollView horizontal showsHorizontalScrollIndicator={false}
            style={{ backgroundColor: '#f8f9fa', maxHeight: 52 }}
            contentContainerStyle={{ paddingHorizontal: 16, alignItems: 'center' }}>
            {EDIT_SECTIONS.map(s => (
              <TouchableOpacity key={s.id} style={[styles.editTab, editSection === s.id && styles.editTabActive]}
                onPress={() => setEditSection(s.id)}>
                <Text style={[styles.editTabText, editSection === s.id && styles.editTabTextActive]}>{s.label}</Text>
              </TouchableOpacity>
            ))}
          </ScrollView>

          <ScrollView style={{ flex: 1, paddingHorizontal: 20 }} keyboardShouldPersistTaps="handled">
            {editSection === 'basic' && (
              <View style={styles.editFormSection}>
                <Field label="Company Name *"        value={editData.name}               onChange={upd('name')}               placeholder="Acme Ltd" />
                <Field label="Industry *"            value={editData.industry}           onChange={upd('industry')}           placeholder="Construction" />
                <Field label="Company Type"          value={editData.companyType}        onChange={upd('companyType')}        placeholder="Ltd, PLC, Sole Trader…" />
                <Field label="Company Size"          value={editData.companySize}        onChange={upd('companySize')}        placeholder="1-10, 11-50, 51-200…" />
                <Field label="Email *"               value={editData.email}              onChange={upd('email')}              placeholder="info@company.com" email />
                <Field label="Founded"               value={editData.founded ?? ''} onChange={upd('founded')} placeholder="2010" numeric />
                <Field label="Annual Revenue (£)"    value={editData.annualRevenue}      onChange={upd('annualRevenue')}      placeholder="500000" numeric />
                <Field label="Currency"              value={editData.currency}           onChange={upd('currency')}           placeholder="GBP" />
                <Field label="Timezone"              value={editData.timezone}           onChange={upd('timezone')}           placeholder="Europe/London" />
                <Field label="Fiscal Year Start"     value={editData.fiscalYearStart}    onChange={upd('fiscalYearStart')}    placeholder="April" />
                <Field label="Company Code (auto)"   value={editData.companyCode}        onChange={() => {}}                 placeholder="Auto-generated" disabled />
                <Field label="Description"           value={editData.description}        onChange={upd('description')}        placeholder="What does your company do?" multiline />
              </View>
            )}

            {editSection === 'contact' && (
              <View style={styles.editFormSection}>
                <Field label="Phone"         value={editData.phone}         onChange={upd('phone')}         placeholder="+44 20 7946 0958" />
                <Field label="Website"       value={editData.website}       onChange={upd('website')}       placeholder="https://www.company.com" url />
                <Field label="Headquarters"  value={editData.headquarters}  onChange={upd('headquarters')}  placeholder="Main office location" />
                <Field label="Street Address" value={editData.address}       onChange={upd('address')}       placeholder="123 High Street" multiline />
                <Field label="City"           value={editData.city}          onChange={upd('city')}          placeholder="London" />
                <Field label="Postcode"       value={editData.postcode}      onChange={upd('postcode')}      placeholder="EC1A 1BB" />
                <Field label="Country"        value={editData.country}       onChange={upd('country')}       placeholder="United Kingdom" />
              </View>
            )}

            {editSection === 'social' && (
              <View style={styles.editFormSection}>
                <Field label="LinkedIn URL"   value={editData.linkedIn}   onChange={upd('linkedIn')}   placeholder="https://linkedin.com/company/…" url />
                <Field label="Twitter Handle" value={editData.twitter}    onChange={upd('twitter')}    placeholder="@company" />
                <Field label="Facebook URL"   value={editData.facebook}   onChange={upd('facebook')}   placeholder="https://facebook.com/company" url />
                <Field label="Instagram"      value={editData.instagram}  onChange={upd('instagram')}  placeholder="@company" />
              </View>
            )}

            {editSection === 'invoice' && (
              <View style={styles.editFormSection}>
                <Field label="Invoice Prefix"         value={editData.invoicePrefix}    onChange={upd('invoicePrefix')}    placeholder="INV" />
                <Field label="Default Tax Rate (%)"   value={editData.defaultTaxRate}   onChange={upd('defaultTaxRate')}   placeholder="20" numeric />
                <Field label="Payment Terms (days)"   value={editData.paymentTerms}     onChange={upd('paymentTerms')}     placeholder="30" numeric />
                <Field label="Invoice Footer"         value={editData.invoiceFooter}    onChange={upd('invoiceFooter')}    placeholder="Thank you for your business." multiline />
                <Field label="Invoice Notes"          value={editData.invoiceNotes}     onChange={upd('invoiceNotes')}     placeholder="All charges in GBP." multiline />
                <View style={styles.inputGroup}>
                  <Text style={styles.inputLabel}>Auto Invoice Numbering</Text>
                  <Switch value={!!editData.autoInvoiceNumbering}
                    onValueChange={v => setEditData(p => ({ ...p, autoInvoiceNumbering: v }))} thumbColor="#0e4e96" />
                </View>
                <View style={styles.inputGroup}>
                  <Text style={styles.inputLabel}>Require PO Number</Text>
                  <Switch value={!!editData.requirePONumber}
                    onValueChange={v => setEditData(p => ({ ...p, requirePONumber: v }))} thumbColor="#0e4e96" />
                </View>
              </View>
            )}

            {editSection === 'bank' && (
              <View style={styles.editFormSection}>
                <Field label="Bank Name"       value={editData.bankName}     onChange={upd('bankName')}     placeholder="Barclays" />
                <Field label="Account Number"  value={editData.bankAccount}  onChange={upd('bankAccount')}  placeholder="12345678" numeric />
                <Field label="Sort Code"       value={editData.bankSortCode} onChange={upd('bankSortCode')} placeholder="20-00-00" />
                <Field label="IBAN"            value={editData.bankIBAN}     onChange={upd('bankIBAN')}     placeholder="GB00 BARC 0000 0000 0000 00" />
                <Field label="SWIFT / BIC"     value={editData.bankSWIFT}    onChange={upd('bankSWIFT')}    placeholder="BARCGB22" />
              </View>
            )}

            {editSection === 'legal' && (
              <View style={styles.editFormSection}>
                <Field label="Registration Number"   value={editData.registrationNumber} onChange={upd('registrationNumber')} placeholder="12345678" />
                <Field label="VAT Number"            value={editData.vatNumber}          onChange={upd('vatNumber')}          placeholder="GB123456789" />
                <Field label="Company Type"          value={editData.companyType}        onChange={upd('companyType')}        placeholder="Ltd, PLC, Sole Trader, Partnership" />
                <Field label="Year Founded"          value={editData.founded ?? ''} onChange={upd('founded')} placeholder="e.g. 2015" numeric />
                <Field label="Insurance Expiry Date" value={editData.insuranceExpiry}    onChange={upd('insuranceExpiry')}    placeholder="YYYY-MM-DD" />
                <View style={styles.inputGroup}>
                  <Text style={styles.inputLabel}>CQC Registered</Text>
                  <View style={{ flexDirection: 'row', alignItems: 'center', gap: 12 }}>
                    <Switch
                      value={!!editData.cqcRegistered}
                      onValueChange={v => setEditData(p => ({ ...p, cqcRegistered: v }))}
                      trackColor={{ false: '#ccc', true: '#0e4e96' }}
                      thumbColor="#fff"
                    />
                    <Text style={{ fontSize: 14, color: editData.cqcRegistered ? '#0e4e96' : '#888' }}>
                      <Text>{editData.cqcRegistered ? 'Registered' : 'Not registered'}</Text>
                    </Text>
                  </View>
                </View>
                <View style={styles.inputGroup}>
                  <Text style={styles.inputLabel}>Compliance Status</Text>
                  <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 8, marginTop: 4 }}>
                    {[
                      { key: 'compliant',     label: 'Compliant',     color: '#10B981' },
                      { key: 'review',        label: 'Under Review',  color: '#F59E0B' },
                      { key: 'non_compliant', label: 'Non-Compliant', color: '#EF4444' },
                    ].map(s => {
                      const active = editData.complianceStatus === s.key;
                      return (
                        <TouchableOpacity
                          key={s.key}
                          style={[styles.compliancePill, { borderColor: s.color, backgroundColor: active ? s.color : '#fff' }]}
                          onPress={() => setEditData(p => ({ ...p, complianceStatus: s.key }))}>
                          <Ionicons name={active ? 'checkmark-circle' : 'ellipse-outline'} size={15} color={active ? '#fff' : s.color} />
                          <Text style={[styles.compliancePillText, { color: active ? '#fff' : s.color }]}>{s.label}</Text>
                        </TouchableOpacity>
                      );
                    })}
                  </View>
                </View>
              </View>
            )}
            <View style={{ height: 40 }} />
          </ScrollView>
        </KeyboardAvoidingView>
      </SafeAreaView>
    </Modal>
  );

  // ─── Invite Modal ────────────────────────────────────────────────────────────
  const renderInviteModal = () => (
    <Modal visible={showInviteModal} animationType="slide" presentationStyle="pageSheet">
      <SafeAreaView style={styles.modalShell} edges={['top', 'bottom']}>
        <View style={styles.modalHeader}>
          <TouchableOpacity onPress={() => setShowInviteModal(false)}>
            <Text style={styles.modalCancel}>Cancel</Text>
          </TouchableOpacity>
          <Text style={styles.modalTitle}>Invite Team Member</Text>
          <TouchableOpacity onPress={sendTeamInvite} disabled={sendingInvite}>
            {sendingInvite ? <ActivityIndicator size="small" color="#0e4e96" />
              : <Text style={styles.modalSave}>Send</Text>}
          </TouchableOpacity>
        </View>
        <View style={{ padding: 20 }}>
          <Text style={styles.inputLabel}>Email Address</Text>
          <TextInput
            style={styles.textInput}
            value={inviteEmail}
            onChangeText={setInviteEmail}
            placeholder="colleague@example.com"
            keyboardType="email-address"
            autoCapitalize="none"
          />
          <Text style={[styles.inputLabel, { marginTop: 20 }]}>Role</Text>
          <View style={{ flexDirection: 'row', gap: 10 }}>
            {['member', 'manager', 'admin'].map(r => (
              <TouchableOpacity key={r}
                style={[styles.pill, inviteRole === r && styles.pillActive]}
                onPress={() => setInviteRole(r)}>
                <Text style={[styles.pillText, inviteRole === r && styles.pillTextActive]}>{r}</Text>
              </TouchableOpacity>
            ))}
          </View>
          <View style={[styles.apiRefBox, { marginTop: 24 }]}>
            <Text style={styles.apiRefLabel}>Company Code</Text>
            <Text style={styles.apiRefValue}>{companyProfile?.companyCode}</Text>
          </View>
          <View style={styles.apiRefBox}>
            <Text style={styles.apiRefLabel}>Invite Link</Text>
            <Text style={styles.apiRefValue} selectable numberOfLines={2}>
              {`https://app.jobeng.ai/join?code=${companyProfile?.companyCode || '...'}`}
            </Text>
          </View>
          <TouchableOpacity style={[styles.primaryBtn, { marginTop: 16 }]} onPress={shareInviteLink}>
            <Ionicons name="share-social-outline" size={20} color="#fff" />
            <Text style={styles.primaryBtnText}>Share Invite Link Instead</Text>
          </TouchableOpacity>
        </View>
      </SafeAreaView>
    </Modal>
  );

  // ─── Root ───────────────────────────────────────────────────────────────────
  if (loading) {
    return (
      <View style={styles.loadingScreen}>
        <ActivityIndicator size={48} color="#0e4e96" />
        <Text style={styles.loadingText}>Loading company profile…</Text>
      </View>
    );
  }

  const renderTabContent = () => {
    switch (activeTab) {
      case 'overview':     return renderOverview();
      case 'team':         return renderTeam();
      case 'invoice':      return renderInvoice();
      case 'bank':         return renderBank();
      case 'legal':        return renderLegal();
      case 'subscription': return renderSubscription();
      case 'activity':     return renderActivity();
      default:             return renderOverview();
    }
  };

  return (
    <View style={styles.root}>
      {renderHeader()}
      {renderHero()}
      {renderTabBar()}
      <View style={{ flex: 1 }}>{renderTabContent()}</View>
      {renderEditModal()}
      {renderPreviewModal()}
      {renderInviteModal()}
    </View>
  );
}

// ─── Styles ──────────────────────────────────────────────────────────────────
const styles = StyleSheet.create({
  root:              { flex: 1, backgroundColor: '#f0f2f5' },
  loadingScreen:     { flex: 1, justifyContent: 'center', alignItems: 'center', backgroundColor: '#f0f2f5' },
  loadingText:       { marginTop: 16, fontSize: 16, color: '#555' },

  // Header
  header:            { paddingTop: 8, paddingBottom: 14 },
  headerRow:         { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', paddingHorizontal: 20 },
  headerBtn:         { padding: 8, borderRadius: 20, backgroundColor: 'rgba(255,255,255,0.18)' },
  headerTitle:       { fontSize: 19, fontWeight: 'bold', color: '#fff' },

  // Hero
  hero:              { paddingHorizontal: 20, paddingBottom: 20 },
  heroContent:       { alignItems: 'center' },
  heroInfo:          { alignItems: 'center', marginTop: 12, width: '100%' },
  heroName:          { fontSize: 22, fontWeight: 'bold', color: '#fff', textAlign: 'center', marginBottom: 4 },
  heroSub:           { fontSize: 13, color: 'rgba(255,255,255,0.8)', textAlign: 'center', marginBottom: 4 },
  heroBadge:         { flexDirection: 'row', alignItems: 'center', backgroundColor: 'rgba(255,255,255,0.2)', paddingHorizontal: 12, paddingVertical: 5, borderRadius: 20, marginVertical: 3 },
  heroBadgeText:     { color: '#fff', fontSize: 12, fontWeight: '600', marginLeft: 5 },
  statusBar:         { flexDirection: 'row', alignItems: 'center', justifyContent: 'center', marginTop: 8 },
  statusText:        { color: '#fff', marginLeft: 8, fontSize: 12 },

  // Logo
  logoContainer:     { position: 'relative', alignItems: 'center', justifyContent: 'center' },
  logoImage:         { borderWidth: 3, borderColor: '#fff' },
  logoPlaceholder:   { alignItems: 'center', justifyContent: 'center', borderWidth: 3, borderColor: '#fff' },
  logoActions:       { position: 'absolute', bottom: -4, right: -4, flexDirection: 'row' },
  logoActionBtn:     { backgroundColor: '#fff', borderRadius: 15, width: 30, height: 30, alignItems: 'center', justifyContent: 'center', elevation: 4, shadowColor: '#000', shadowOffset: { width: 0, height: 2 }, shadowOpacity: 0.2, shadowRadius: 3 },
  syncBadge:         { position: 'absolute', top: -4, right: -4, backgroundColor: '#EF4444', borderRadius: 10, width: 18, height: 18, alignItems: 'center', justifyContent: 'center' },

  // Tabs
  tabBar:            { backgroundColor: '#fff', borderBottomWidth: 1, borderBottomColor: '#e8e8e8' },
  tab:               { flexDirection: 'row', alignItems: 'center', paddingHorizontal: 16, paddingVertical: 13, borderBottomWidth: 2, borderBottomColor: 'transparent' },
  tabActive:         { borderBottomColor: '#0e4e96' },
  tabText:           { marginLeft: 6, fontSize: 13, color: '#888' },
  tabTextActive:     { color: '#0e4e96', fontWeight: '600' },
  tabContent:        { flex: 1, backgroundColor: '#f0f2f5' },

  // Section
  section:           { backgroundColor: '#fff', marginHorizontal: 14, marginVertical: 7, borderRadius: 12, padding: 18, elevation: 2, shadowColor: '#000', shadowOffset: { width: 0, height: 1 }, shadowOpacity: 0.08, shadowRadius: 4 },
  sectionTitle:      { fontSize: 17, fontWeight: 'bold', color: '#222', marginBottom: 14 },
  sectionTitleRow:   { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 },

  // Info rows
  infoRow:           { flexDirection: 'row', alignItems: 'center', marginBottom: 14 },
  infoIcon:          { width: 44, height: 44, borderRadius: 22, backgroundColor: '#e8f0fb', alignItems: 'center', justifyContent: 'center', marginRight: 14 },
  infoContent:       { flex: 1 },
  infoLabel:         { fontSize: 11, color: '#888', marginBottom: 2 },
  infoValue:         { fontSize: 15, color: '#0e4e96', fontWeight: '600' },
  copyBtn:           { padding: 6 },
  descriptionBox:    { backgroundColor: '#f8f9fa', borderRadius: 8, padding: 14, marginTop: 4 },
  descriptionLabel:  { fontSize: 12, color: '#888', marginBottom: 4 },
  descriptionText:   { fontSize: 14, color: '#333', lineHeight: 20 },

  // Setting rows
  settingRow:        { flexDirection: 'row', justifyContent: 'space-between', paddingVertical: 8, borderBottomWidth: 1, borderBottomColor: '#f0f0f0' },
  settingLabel:      { fontSize: 14, color: '#666' },
  settingValue:      { fontSize: 14, color: '#0e4e96', fontWeight: '600' },
  multilineBox:      { backgroundColor: '#f8f9fa', borderRadius: 8, padding: 12, marginBottom: 10 },
  multilineLabel:    { fontSize: 12, color: '#888', marginBottom: 4 },
  multilineValue:    { fontSize: 14, color: '#333', lineHeight: 20 },
  toggleRow:         { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', paddingVertical: 10, borderBottomWidth: 1, borderBottomColor: '#f0f0f0' },
  toggleLabel:       { fontSize: 14, color: '#333' },

  // Stats
  statsRow:          { flexDirection: 'row', paddingHorizontal: 14, paddingTop: 12, gap: 8 },
  statCard:          { flex: 1, backgroundColor: '#fff', borderRadius: 12, padding: 14, alignItems: 'center', elevation: 2, shadowColor: '#000', shadowOffset: { width: 0, height: 1 }, shadowOpacity: 0.07, shadowRadius: 4 },
  statValue:         { fontSize: 18, fontWeight: 'bold', color: '#0e4e96', marginTop: 6 },
  statLabel:         { fontSize: 11, color: '#888', marginTop: 2, textAlign: 'center' },

  // Company code
  codeCard:          { backgroundColor: '#f0f4ff', borderRadius: 12, padding: 18 },
  codeBox:           { backgroundColor: '#0e4e96', borderRadius: 10, paddingVertical: 14, paddingHorizontal: 24, alignItems: 'center', marginBottom: 12 },
  codeText:          { fontSize: 28, fontWeight: 'bold', color: '#fff', letterSpacing: 4 },
  codeHint:          { fontSize: 13, color: '#555', textAlign: 'center', marginBottom: 6 },
  codeLink:          { fontSize: 11, color: '#0e4e96', textAlign: 'center', marginBottom: 14 },
  codeActions:       { flexDirection: 'row', justifyContent: 'space-around' },
  codeActionBtn:     { flexDirection: 'row', alignItems: 'center', paddingVertical: 8, paddingHorizontal: 14, borderRadius: 8, backgroundColor: '#fff', borderWidth: 1, borderColor: '#dde8ff' },
  codeActionText:    { marginLeft: 6, fontSize: 13, color: '#0e4e96', fontWeight: '600' },

  // Team
  inviteCodeRow:     { flexDirection: 'row', alignItems: 'center', marginBottom: 12, gap: 10 },
  inviteCodeBox:     { flex: 1, flexDirection: 'row', alignItems: 'center', backgroundColor: '#f0f4ff', borderRadius: 10, paddingVertical: 12, paddingHorizontal: 16, gap: 10 },
  inviteCodeValue:   { fontSize: 18, fontWeight: 'bold', color: '#0e4e96', letterSpacing: 2 },
  inviteShareBtn:    { backgroundColor: '#0e4e96', borderRadius: 10, paddingVertical: 12, paddingHorizontal: 20, flexDirection: 'row', alignItems: 'center', gap: 6 },
  inviteShareBtnText:{ color: '#fff', fontWeight: '600', fontSize: 15 },
  primaryBtn:        { backgroundColor: '#0e4e96', borderRadius: 10, paddingVertical: 13, flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: 8 },
  primaryBtnText:    { color: '#fff', fontWeight: 'bold', fontSize: 15 },
  memberCard:        { flexDirection: 'row', alignItems: 'center', paddingVertical: 12, borderBottomWidth: 1, borderBottomColor: '#f0f0f0' },
  memberAvatar:      { width: 48, height: 48, borderRadius: 24, overflow: 'hidden', marginRight: 14 },
  memberAvatarImg:   { width: 48, height: 48 },
  memberAvatarPlaceholder: { width: 48, height: 48, alignItems: 'center', justifyContent: 'center' },
  memberAvatarLetter:{ fontSize: 20, fontWeight: 'bold', color: '#fff' },
  memberInfo:        { flex: 1 },
  memberName:        { fontSize: 15, fontWeight: '600', color: '#222' },
  memberEmail:       { fontSize: 12, color: '#888', marginTop: 2 },
  roleBadge:         { alignSelf: 'flex-start', paddingHorizontal: 8, paddingVertical: 2, borderRadius: 8, marginTop: 4 },
  roleBadgeText:     { fontSize: 10, fontWeight: 'bold', color: '#fff' },
  memberMeta:        { alignItems: 'flex-end', gap: 4 },
  memberStatus:      { fontSize: 12, color: '#10B981', fontWeight: '600' },
  memberJoined:      { fontSize: 11, color: '#aaa' },

  // Bank
  bankCard:          { backgroundColor: '#f8f9fa', borderRadius: 10, padding: 18, alignItems: 'center' },
  bankRow:           { flexDirection: 'row', alignItems: 'center', width: '100%', paddingVertical: 9, borderBottomWidth: 1, borderBottomColor: '#eee' },
  bankLabel:         { fontSize: 13, color: '#888', width: 130 },
  bankValue:         { fontSize: 14, color: '#0e4e96', fontWeight: '600' },

  // Legal
  complianceBanner:  { flexDirection: 'row', alignItems: 'center', margin: 14, borderRadius: 12, padding: 18 },
  complianceBannerTitle: { fontSize: 17, fontWeight: 'bold', color: '#fff' },
  complianceBannerSub:   { fontSize: 12, color: 'rgba(255,255,255,0.8)', marginTop: 2 },
  compliancePill:    { flexDirection: 'row', alignItems: 'center', paddingHorizontal: 14, paddingVertical: 9, borderRadius: 20, borderWidth: 1.5, gap: 6 },
  compliancePillText:{ fontSize: 13, fontWeight: '600' },
  legalEmptyField:   { flexDirection: 'row', alignItems: 'center', paddingVertical: 14, paddingHorizontal: 10, backgroundColor: '#f9fafb', borderRadius: 8, marginBottom: 10, borderWidth: 1, borderColor: '#e8e8e8', borderStyle: 'dashed', gap: 10 },
  legalEmptyFieldText: { flex: 1, fontSize: 14, color: '#aaa' },
  documentRow:       { flexDirection: 'row', alignItems: 'center', paddingVertical: 14, borderBottomWidth: 1, borderBottomColor: '#f0f0f0' },
  documentLabel:     { fontSize: 14, color: '#333', fontWeight: '500' },
  documentStatus:    { fontSize: 12, color: '#888', marginTop: 2 },

  // Plan
  planCard:          { margin: 14, borderRadius: 14, padding: 24, alignItems: 'center' },
  planName:          { fontSize: 22, fontWeight: 'bold', color: '#fff', marginTop: 10 },
  planPrice:         { fontSize: 16, color: 'rgba(255,255,255,0.85)', marginTop: 4 },
  featureRow:        { flexDirection: 'row', alignItems: 'center', paddingVertical: 8, borderBottomWidth: 1, borderBottomColor: '#f0f0f0' },
  featureText:       { marginLeft: 10, fontSize: 14, color: '#333', textTransform: 'capitalize' },

  // Activity
  activityRow:       { flexDirection: 'row', alignItems: 'flex-start', paddingVertical: 10, borderBottomWidth: 1, borderBottomColor: '#f0f0f0' },
  activityDot:       { width: 10, height: 10, borderRadius: 5, marginTop: 5 },
  activityTitle:     { fontSize: 14, color: '#333', fontWeight: '500' },
  activityMeta:      { fontSize: 12, color: '#aaa', marginTop: 2 },

  // API ref
  apiRefBox:         { backgroundColor: '#f0f4ff', borderRadius: 8, padding: 12, marginBottom: 10 },
  apiRefLabel:       { fontSize: 11, color: '#666', marginBottom: 4 },
  apiRefValue:       { fontSize: 13, color: '#0e4e96', fontWeight: '600', fontFamily: Platform.OS === 'ios' ? 'Courier' : 'monospace' },

  // Invoice preview
  invoicePreview:    { backgroundColor: '#f9f9f9', borderRadius: 8, padding: 16, borderWidth: 1, borderColor: '#e8e8e8' },
  invoicePreviewHeader: { flexDirection: 'row', alignItems: 'center', marginBottom: 14, gap: 14 },
  invoicePreviewLogo:   { width: 52, height: 52, borderRadius: 8 },
  invoicePreviewLogoPlaceholder: { width: 52, height: 52, borderRadius: 8, backgroundColor: '#e8f0fb', alignItems: 'center', justifyContent: 'center' },
  invoicePreviewCompany: { fontSize: 16, fontWeight: 'bold', color: '#0e4e96' },
  invoicePreviewSub:     { fontSize: 12, color: '#666', marginTop: 2 },
  invoicePreviewBody:    { borderTopWidth: 1, borderTopColor: '#e8e8e8', paddingTop: 12 },
  invoicePreviewNumber:  { fontSize: 18, fontWeight: 'bold', color: '#222', marginBottom: 4 },
  invoicePreviewMeta:    { fontSize: 12, color: '#888' },
  invoicePreviewFooter:  { marginTop: 14, borderTopWidth: 1, borderTopColor: '#e8e8e8', paddingTop: 10 },
  invoicePreviewFooterText: { fontSize: 12, color: '#666', fontStyle: 'italic' },

  // Empty state
  emptyState:        { alignItems: 'center', paddingVertical: 32 },
  emptyText:         { fontSize: 16, fontWeight: '600', color: '#aaa', marginTop: 12 },
  emptySubText:      { fontSize: 13, color: '#ccc', marginTop: 6, textAlign: 'center' },

  // Modal
  modalShell:        { flex: 1, backgroundColor: '#fff' },
  modalHeader:       { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', paddingHorizontal: 20, paddingVertical: 14, borderBottomWidth: 1, borderBottomColor: '#e8e8e8' },
  modalCancel:       { fontSize: 16, color: '#888' },
  modalTitle:        { fontSize: 17, fontWeight: 'bold', color: '#222' },
  modalSave:         { fontSize: 16, color: '#0e4e96', fontWeight: '700' },
  editTab:           { paddingHorizontal: 16, paddingVertical: 8, borderRadius: 20, marginRight: 6 },
  editTabActive:     { backgroundColor: '#0e4e96' },
  editTabText:       { fontSize: 13, color: '#666' },
  editTabTextActive: { color: '#fff', fontWeight: '600' },
  editFormSection:   { paddingVertical: 20 },
  inputGroup:        { marginBottom: 18 },
  inputLabel:        { fontSize: 14, fontWeight: '600', color: '#333', marginBottom: 7 },
  textInput:         { borderWidth: 1, borderColor: '#e0e0e0', borderRadius: 10, paddingHorizontal: 14, paddingVertical: 11, fontSize: 15, backgroundColor: '#fff', color: '#222' },
  textArea:          { height: 90, textAlignVertical: 'top' },
  disabledInput:     { backgroundColor: '#f5f5f5', color: '#999' },
  myProfileBanner:   { flexDirection: 'row', alignItems: 'center', backgroundColor: '#EFF6FF',
    marginHorizontal: 14, marginBottom: 12, borderRadius: 14, padding: 16,
    borderWidth: 1.5, borderColor: '#BFDBFE' },

  pill:              { paddingHorizontal: 16, paddingVertical: 8, borderRadius: 20, borderWidth: 1.5, borderColor: '#ddd' },
  pillActive:        { backgroundColor: '#0e4e96', borderColor: '#0e4e96' },
  pillText:          { fontSize: 13, color: '#666', textTransform: 'capitalize' },
  pillTextActive:    { color: '#fff', fontWeight: '600' },
});